<template>
  <div class="container-fluid">
    <!-- ----------------------------- -->
    <!-- OVERLAY: combatant side panel -->
    <!-- ----------------------------- -->
    <CombatantSidePanel
      v-if="isSidePanelOpen"
      :combatant="combatant"
      :combatantNames="combatantNames"
    />
    <PartySkillsView v-if="showSkillsViewer" />
    <PartyRunesView v-if="showPartyRunesViewer" />
    <LogSidePanel v-if="showLogPanel" />
    <!-- ------------------------ -->
    <!-- CONFIRMATIONS            -->
    <!-- ----------------------- -->
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <!-- ------------------------ -->
    <!-- NOTIFICATIONS            -->
    <!-- ------------------------ -->
    <notifications
      width="500"
      group="bb-confirm"
      :speed="100"
      :duration="1000"
      position="top right"
      classes="vue-notification"
    />
    <!-- ------------------- -->
    <!-- DEBUG ROW            -->
    <!-- ------------------- -->
    <div
      v-if="debug"
      class="justify-content-center text-center"
    >
      <span class="bb-debug mt-2 mb-2">
        <p>
          [ loadReady = {{ loadReady }} ]--[ rolled = {{ arena_rolled }} ]--[
          ended = {{ arena_ended }} ]--[ in progress =
          {{ arena_in_progress }} ]--[ unused = {{ unused_action_count }} ]
          --[extra ready = {{ arena_extra_ready }} ] --[ extra action ={{
            arena_extra_action
          }}
          ] --[ bleeders ready ={{ arena_bleeders_ready }} ]
        </p>
        <p>
          [ stored cur action = {{ stored_cur_action }} ]--[ round completed =
          {{ arena_round_completed }} ]--[ toggle desc = {{ toggleDesc }} ]--[
          toggle numbers = {{ toggleNumbers }} ]--[ toggle extras =
          {{ toggleExtras }} ]--[ filter status = {{ filterStatus }} ]
        </p>
      </span>
    </div>
    <!-- ------------------- -->
    <!-- MAIN ROW            -->
    <!-- ------------------- -->
    <div
      v-if="loadReady"
      class="row"
    >
      <!-- ------------------- -->
      <!-- COL1: buttons panel -->
      <!-- ------------------- -->
      <div class="col-md-1 rounded">
        <CombatButtonsPanel
          :filterStatus="filterStatus"
          :arena_active_combatants="arena_active_combatants"
          :arena_inactive_combatants="arena_inactive_combatants"
          :arena_status="arena_status"
          :arena_round="arena_round"
          :arena_action="arena_action"
          :view_round="view_round"
          :arena_rolled="arena_rolled"
          :arena_extra_ready="arena_extra_ready"
          :arena_extra_action="arena_extra_action"
          :arena_bleeders_ready="arena_bleeders_ready"
          :arena_round_completed="arena_round_completed"
          :arena_in_progress="arena_in_progress"
          :outstanding_rolls="outstanding_rolls"
          :arena_locked="arena_locked"
          :arena_ended="arena_ended"
          :race_count="race_count"
          :toggleDesc="toggleDesc"
          :toggleNumbers="toggleNumbers"
        />
      </div>
      <!-- --------------------- -->
      <!-- COL2: combatant table -->
      <!-- --------------------- -->
      <div class="col-xl-10">
        <div
          class="row bb-previous-round-surface"
          :class="{
            'bb-previous-round-surface-disabled': tableOverlayDisabled,
          }"
        >
          <div
            v-if="combatTextOverlayVisible"
            :key="combatTextOverlayKey"
            class="bb-combat-text-overlay"
            aria-live="polite"
          >
            <div class="bb-combat-text-overlay__content">
              {{ combatTextOverlayMessage }}
            </div>
          </div>
          <TableCombatants
            :headings="headings"
            :combatants="combatants"
            :selected_action="selected_action"
            :selected_reaction="selected_reaction"
            :selected_marker="selected_marker"
            :selected_number="selected_number"
            :arena_round="arena_round"
            :view_round="view_round"
            :arena_action="arena_action"
            :toggleDesc="toggleDesc"
            :toggleNumbers="toggleNumbers"
            :arena_status="arena_status"
            :arena_rolled="arena_rolled"
            :arena_in_progress="arena_in_progress"
            :arena_extra_ready="arena_extra_ready"
            :arena_extra_action="arena_extra_action"
            :arena_round_completed="arena_round_completed"
            :filterStatus="filterStatus"
            :minBleedCount="minBleedCount"
            :maxBleedCount="maxBleedCount"
          />
        </div>
        <!-- ------------------- -->
        <!-- MARKERS ROW         -->
        <!-- ------------------- -->
        <div
          v-if="showMarkersPanel"
          class="col"
        >
          <div class="row mt-3 mb-3"></div>
          <div class="row mt-3 mb-3"></div>
          <div class="row w-100 mt-3">
            <MarkersPanel
              :markers="markers"
              :disabled="areMarkersDisabled"
              :previous_round_overlay="!toggleActions"
              :arena_active_combatants="arena_active_combatants"
              :arena_extra_action="arena_extra_action"
              :arena_in_progress="arena_in_progress"
              :arena_rolled="arena_rolled"
              :arena_round="arena_round"
              :outstanding_rolls="outstanding_rolls"
              :toggleNumbers="toggleNumbers"
              :view_round="view_round"
            />
          </div>
        </div>
      </div>
      <!-- ------------------------- -->
      <!-- COL3 (A): actions panel   -->
      <!-- ------------------------- -->
      <div
        v-if="gotCombatants && loadActionsReady && !toggleNumbers"
        class="col-xl-1"
        style="right: 2em; position: fixed; z-index: 2000"
      >
        <ActionsPanel
          :actions="actions"
          :reactions="reactions"
          :arena_extra_action="arena_extra_action"
          :arena_round="arena_round"
          :view_round="view_round"
          :disabled="!arena_in_progress || !arena_rolled || !toggleActions"
          :reactions_disabled="
            !arena_in_progress || !arena_rolled || view_round != arena_round
          "
        />
      </div>
      <!-- ------------------------- -->
      <!-- COL3 (B): number panel    -->
      <!-- ------------------------- -->
      <div
        v-if="toggleNumbers && arena_extra_action == 0"
        class="col-xl-1"
        style="right: 2em; position: fixed"
      >
        <NumberPanel />
      </div>
    </div>
    <div v-if="!loadReady">
      <span class="bb-loader-text">{{ loading_msg }}</span>
      <div class="bb-loader"></div>
    </div>
  </div>
</template>

<script>
//========================================================
// scripts
//---------
import { inject } from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import store from '../store/index'

//------------
// components
//------------
import CombatantSidePanel from '../components/CombatantSidePanel.vue'
import CombatButtonsPanel from '../components/CombatButtonsPanel.vue'
import TableCombatants from '../components/TableCombatants.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import MarkersPanel from '../components/MarkersPanel.vue'
import ActionsPanel from '../components/ActionsPanel.vue'
import LogSidePanel from '../components/LogSidePanel.vue'
import NumberPanel from '../components/NumberPanel.vue'
import PartySkillsView from './PartySkillsView.vue'
import PartyRunesView from './PartyRunesView.vue'

//-------------
// composables
//-------------
import useUpdateMultipleCombatants from '../composables/useUpdateMultipleCombatants.js'
import useAddMultipleCombatants from '../composables/useAddMultipleCombatants.js'
import useClearCampaignCombatants from '../composables/useClearCampaignCombatants.js'
import useDeleteCombatantSlots from '../composables/useDeleteCombatantSlots.js'
import useUpdateArenaProgress from '../composables/useUpdateArenaProgress.js'
import useEditCombatReaction from '../composables/useEditCombatReaction.js'
import useEditCombatAction from '../composables/useEditCombatAction.js'
import useEditCombatMarker from '../composables/useEditCombatMarker.js'
import useUpdateCombatant from '../composables/useUpdateCombatant.js'
import useDeleteCombatant from '../composables/useDeleteCombatant.js'
import useEditCombatant from '../composables/useEditCombatant.js'
import useDeleteSlot from '../composables/useDeleteSlot.js'

//------------------
// helpers
//------------------
import loadSpecificCombatantHeadlineMarkers from '../helpers/loadSpecificCombatantHeadlineMarkers'
import resolveCharacterForCombatDeploy from '../helpers/resolveCharacterForCombatDeploy'
import loadSpecificCombatantExtraImage from '../helpers/loadSpecificCombatantExtraImage'
import loadSpecificCombatantReactions from '../helpers/loadSpecificCombatantReactions'
import loadSpecificCombatantActions from '../helpers/loadSpecificCombatantActions'
import clearLocalCombatantDupeRanks from '../helpers/clearLocalCombatantDupeRanks'

import loadCombatantHeadlineMarkers from '../helpers/loadCombatantHeadlineMarkers'
import loadSpecificCombatantImages from '../helpers/loadSpecificCombatantImages'
import getCombatantFilterIndexById from '../helpers/getCombatantFilterIndexById'
import convertCharacterToCombatant from '../helpers/convertCharacterToCombatant'
import setLocalCombatantsIsRanked from '../helpers/setLocalCombatantsIsRanked'
import hasMissingCombatantActions from '../helpers/hasMissingCombatantActions'

import clearLocalCombatantRanks from '../helpers/clearLocalCombatantRanks'
import setLocalCombatantsExtras from '../helpers/setLocalCombatantsExtras'
import loadCombatantExtraImages from '../helpers/loadCombatantExtraImages'
import loadLocalCombatantProps from '../helpers/loadLocalCombatantProps'
import clearCampaignCombatants from '../helpers/clearCampaignCombatants'
import hasOutstandingRollOffs from '../helpers/hasOutstandingRollOffs'
import loadCombatantsSRImages from '../helpers/loadCombatantsSRImages'
import loadCampaignRoundSlots from '../helpers/loadCampaignRoundSlots'

import hasOutstandingSRRolls from '../helpers/hasOutstandingSRRolls'
import refreshCombatantOrder from '../helpers/refreshCombatantOrder'
import getCombatantIndexById from '../helpers/getCombatantIndexById'
import getUnusedExtraActions from '../helpers/getUnusedExtraActions'
import shiftPendingCAsRight from '../helpers/shiftPendingCAsRight'
import autoRollCombatantSRs from '../helpers/autoRollCombatantSRs'
import shiftPendingCAsLeft from '../helpers/shiftPendingCAsLeft'

import reduceReactionCount from '../helpers/reduceReactionCount'
import loadCombatantImages from '../helpers/loadCombatantImages'
import getFilterStatusDesc from '../helpers/getFilterStatusDesc'
import reduceCountersCount from '../helpers/reduceCountersCount'
import getFirstPendingSlot from '../helpers/getFirstPendingSlot'
import shiftCAsForDeletion from '../helpers/shiftCAsForDeletion'

import extractPlayerNames from '../helpers/extractPlayerNames'
import reduceMarkersCount from '../helpers/reduceMarkersCount'
import getToggleExtraCols from '../helpers/getToggleExtraCols'
import getDescSummaryCols from '../helpers/getDescSummaryCols'
import getDescDetailCols from '../helpers/getDescDetailCols'
import reduceActionCount from '../helpers/reduceActionCount'

import hasDuplicateRanks from '../helpers/hasDuplicateRanks'
import getDuplicateRanks from '../helpers/getDuplicateRanks'
import autoTroopRollOffs from '../helpers/autoTroopRollOffs'
import showNotification from '../helpers/showNotification'
import getCombatantById from '../helpers/getCombatantById'
import updateMarkerSlot from '../helpers/updateMarkerSlot'
import getCurrentAction from '../helpers/getCurrentAction'

import getCombatantExtraAttackInvalidReason from '../helpers/getCombatantExtraAttackInvalidReason'
import getCombatantPreviousRoundActionImage from '../helpers/getCombatantPreviousRoundActionImage'
import getCombatantLastRoundActionImage from '../helpers/getCombatantLastRoundActionImage'
import getCombatantNextRoundAction from '../helpers/getCombatantNextRoundAction'
import getCombatantNextRoundMarker from '../helpers/getCombatantNextRoundMarker'

import getArenaSubStatus from '../helpers/getArenaSubStatus'
import changeViewRound from '../helpers/changeViewRound'
import getExtraAttacks from '../helpers/getExtraAttacks'
import storeHiddenCols from '../helpers/storeHiddenCols'
import getToggleSRCols from '../helpers/getToggleSRCols'
import getNumberImage from '../helpers/getNumberImage'
import changePayload from '../helpers/changePayload'
import fillBlankCRs from '../helpers/fillBlankCRs'

import shouldNotifyExtraAttacksReady from '../helpers/shouldNotifyExtraAttacksReady'
import formatCombatantUpdateMessage from '../helpers/formatCombatantUpdateMessage'
import buildCombatLogDescription from '../helpers/buildCombatLogDescription'
import shouldDisableMarkersPanel from '../helpers/shouldDisableMarkersPanel'
import shouldShowMarkersPanel from '../helpers/shouldShowMarkersPanel'
import createSocketConnection from '../helpers/createSocketConnection'
import formatHiddenRollValue from '../helpers/formatHiddenRollValue'
import formatCombatMessage from '../helpers/formatCombatMessage'
import canEditViewedRound from '../helpers/canEditViewedRound'

import updateCASlot from '../helpers/updateCASlot'
import updateCRSlot from '../helpers/updateCRSlot'
import setPendingCA from '../helpers/setPendingCA'
import setPendingCR from '../helpers/setPendingCR'
import setViewRound from '../helpers/setViewRound'
import getSurprised from '../helpers/getSurprised'
import getPrevious from '../helpers/getPrevious'
import handleError from '../helpers/handleError'

import getReaction from '../helpers/getReaction'
import hasBleeders from '../helpers/hasBleeders'
import getBleeders from '../helpers/getBleeders'
import getAction from '../helpers/getAction'
import getMarker from '../helpers/getMarker'
import hasDelays from '../helpers/hasDelays'
import newRound from '../helpers/newRound'
import calcRank from '../helpers/calcRank'
import getNext from '../helpers/getNext'

//------
// main
//------
export default {
  name: 'combatView',
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // components
  //------------
  components: {
    CombatantSidePanel,
    CombatButtonsPanel,
    TableCombatants,
    ConfirmDialog,
    ActionsPanel,
    MarkersPanel,
    NumberPanel,
    PartySkillsView,
    PartyRunesView,
    LogSidePanel,
  },
  //========================================================
  // props
  //-------
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      name: 'CombatantList',
      endpoint: '',
      local_slug: '',
      loading_msg: 'loading characters...',
      debug: false,
      deleteInd: false,
      filterStatus: false,
      isSidePanelOpen: false,
      loadReady: false,
      loadActionsReady: false,
      isInitialisingCombat: false,
      forceRefilterCheck: false,
      deletedCombatantElem: false,
      toggleDesc: false,
      toggleNumbers: false,
      toggleExtras: false,
      arena_locked: false,
      arena_extra_ready: false,
      arena_bleeders_ready: false,
      arena_bleeders_shown: false,
      arena_rolled: false,
      arena_round_completed: false,
      arena_in_progress: false,
      arena_extra_action_ready: false,
      arena_ended: false,
      arena_extra_action: 0,
      arena_extra_action_save: 0,
      unused_action_count: 0,
      delete_index: 0,
      new_col: 0,
      arena_status: '',
      arena_slug: '',
      arena_round: 0,
      arena_action: 0,
      lastLoggableActionNumber: 0,
      arenaSocketConnection: null,
      combatTextOverlayVisible: false,
      combatTextOverlayMessage: '',
      combatTextOverlayKey: 0,
      combatTextOverlayTimer: null,
      view_round: 0,
      maxCA: 0,
      maxCR: 0,
      selected_number: 0,
      selected_action: null,
      selected_reaction: null,
      selected_marker: null,
      action_counts: [],
      dupes: [],
      last_selected_action: {
        id: null,
        ca: 0,
      },
      last_selected_reaction: {
        id: null,
        cr: 0,
      },
      races: [],
      roles: [],
      categories: [],
      combatants: [],
      actions: [],
      reactions: [],
      markers: [],
      errors: [],
      errorFields: [],
      curActiveColInds: [],
      selected_number_image: null,
      combatant: null,
      config: null,
      arena: null,
      //----------
      // headings
      //----------
      headings: [
        // idx: 0
        {
          id: 1,
          label: '',
          field: 'image',
          sortable: false,
          tdClass: 'text-center',
          hidden: false,
        },
        // idx: 1
        {
          id: 2,
          label: 'Seq',
          field: 'seq',
          width: '60px',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          formatFn: this.global.hideZeroMinusValues,
          hidden: false,
        },
        // idx: 2
        {
          id: 3,
          label: 'Name',
          field: 'name',
          width: '120px',
          tdClass: 'text-center',
          sortable: false,
          hidden: false,
        },
        // idx: 3
        {
          id: 4,
          label: 'Player',
          field: 'player_name',
          tdClass: 'text-center',
          width: '80px',
          sortable: false,
        },
        // idx: 4
        {
          id: 5,
          label: 'Status',
          field: 'sub_status_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '100px',
          hidden: false,
        },
        // idx: 5
        {
          id: 6,
          label: 'Visibility',
          field: 'visibility_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '100px',
          hidden: true,
        },
        // idx: 6
        {
          id: 7,
          label: 'Fatigue',
          field: 'fatigue_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '100px',
          hidden: false,
        },
        // idx: 7
        {
          id: 8,
          label: 'Surprise',
          field: 'surprise_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '100px',
          hidden: false,
        },
        // idx: 8
        {
          id: 9,
          label: 'Bleed',
          field: 'bleeding_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '100px',
          hidden: false,
        },
        // idx: 9
        {
          id: 10,
          label: 'Options',
          field: 'options_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '168px',
          hidden: false,
        },
        // idx: 10
        {
          id: 11,
          label: 'Markers',
          field: 'markers_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '118px',
          hidden: false,
        },
        // idx: 11
        {
          id: 12,
          label: 'Category',
          field: 'category',
          sortable: false,
          tdClass: 'text-center',
          hidden: false,
        },
        // idx: 12
        {
          id: 13,
          label: 'Type',
          field: 'type',
          tdClass: 'text-center',
          hidden: false,
        },
        // idx: 13
        {
          id: 14,
          label: 'Race',
          field: 'race_name',
          tdClass: 'text-center',
          hidden: false,
        },
        // idx: 14
        {
          id: 15,
          label: 'Role',
          field: 'role',
          tdClass: 'text-center',
          hidden: false,
          sortable: false,
          formatFn: this.global.hideDefaultValues,
        },
        // idx: 15
        {
          id: 16,
          label: 'Status',
          field: 'status',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        // idx: 16
        {
          id: 17,
          label: 'Sub Status',
          field: 'sub_status',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        // idx: 17
        {
          id: 18,
          label: 'DEX',
          field: 'dex',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          hidden: false,
        },
        // idx: 18
        {
          id: 19,
          label: 'CA',
          field: 'ca',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          hidden: false,
        },
        // idx: 19
        {
          id: 20,
          label: 'CR',
          field: 'cr',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          hidden: false,
        },
        {
          id: 21,
          label: 'Dam',
          field: 'damage_mod',
          tdClass: 'text-center',
          sortable: false,
          hidden: false,
          formatFn: this.global.hideZeroMinusValues,
        },
        // idx: 31
        {
          id: 22,
          label: '',
          field: 'fill4',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill-wide',
          thClass: 'bb-is-fill-wide',
          width: '1px',
          hidden: false,
        },
        // idx: 20
        {
          id: 29,
          label: 'Rank',
          field: 'rank',
          type: 'number',
          tdClass: 'text-center',
          formatFn: this.global.hideZeroMinusValues,
          sortable: false,
          hidden: false,
        },
        // idx: 21
        {
          id: 23,
          label: 'SR',
          field: 'sr',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          formatFn: this.global.hideZeroMinusValues,
          hidden: false,
        },
        // idx: 22
        {
          id: 24,
          label: 'SR Adj',
          field: 'sr_adj',
          type: 'number',
          tdClass: 'text-center',
          formatFn: this.global.hideZeroMinusValues,
          sortable: false,
          hidden: false,
        },
        // idx: 23
        {
          id: 25,
          label: 'SR Roll',
          field: 'sr_roll',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          formatFn: this.global.hideZeroMinusValues,
          hidden: false,
        },
        // idx: 24
        {
          id: 26,
          label: 'Roll Off',
          field: 'roll_off',
          type: 'number',
          tdClass: 'text-center',
          formatFn: this.global.hideZeroMinusValues,
          sortable: false,
          hidden: false,
        },
        {
          id: 34,
          label: '',
          field: 'fill1',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill-wide',
          thClass: 'bb-is-fill-wide',
          width: '1px',
          hidden: false,
        },
        // idx: 25
        {
          id: 27,
          label: 'SR Roll',
          field: 'sr_roll_image',
          tdClass: 'text-center',
          sortable: false,
          hidden: true,
        },
        // idx: 26
        {
          id: 28,
          label: 'Roll Off',
          field: 'roll_off_image',
          tdClass: 'text-center',
          sortable: false,
          hidden: true,
        },
        // idx: 27
        {
          id: 30,
          label: 'Action Count',
          field: 'action_count',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        // idx: 28
        {
          id: 31,
          label: 'Actual Action Count',
          field: 'actual_action_count',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        // idx: 29
        {
          id: 32,
          label: 'Marker Count',
          field: 'marker_count',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        // idx: 30
        {
          id: 33,
          label: 'Reaction Count',
          field: 'reaction_count',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        // idx: 31
        {
          id: 34,
          label: '',
          field: 'fill_anchor',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill-wide',
          thClass: 'bb-is-fill-wide',
          width: '1px',
          hidden: false,
        },
        // idx: 32
        //----------------------------
        // actions + reaction here...
        //----------------------------
        {
          id: 90,
          label: 'Visibility',
          field: 'visibility',
          sortable: false,
          tdClass: 'text-center',
          hidden: true,
        },

        {
          id: 91,
          label: 'Fatigue',
          field: 'fatigue',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 92,
          label: 'Bleeding',
          field: 'bleeding',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 93,
          label: 'Surprised',
          field: 'surprised',
          type: 'boolean',
          tdClass: 'text-center',
          hidden: true,
        },

        {
          id: 100,
          label: 'Counters',
          field: 'counters',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 110,
          label: 'Id',
          field: 'id',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 111,
          label: 'Slug',
          field: 'slug',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 112,
          label: 'Dupe Rank',
          field: 'dupe_rank',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 113,
          label: 'Is Ranked',
          field: 'is_ranked',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 114,
          label: 'Headline Marker 1',
          field: 'headline_marker1',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 115,
          label: 'Headline Marker 2',
          field: 'headline_marker2',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 116,
          label: 'Headline Marker 3',
          field: 'headline_marker3',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 117,
          label: 'Headline Marker 4',
          field: 'headline_marker4',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 118,
          label: 'Extra Ind',
          field: 'extra',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 119,
          label: 'Second Weapon',
          field: 'second_weapon',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 120,
          label: 'Shield',
          field: 'shield',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 121,
          label: 'Ranged',
          field: 'ranged',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 122,
          label: 'Magic',
          field: 'magic',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 123,
          label: 'Special Action',
          field: 'special_action',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 124,
          label: 'Special Reaction',
          field: 'special_reaction',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 125,
          label: 'Dupe Processed',
          field: 'dupe_processed',
          tdClass: 'text-center',
          hidden: true,
        },
      ],
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    arenaSubStatus: {
      immediate: true,
      handler(nextStatus) {
        this.setArenaSubStatus(nextStatus)
      },
    },
    arena_bleeders_ready() {},
    //========================================================
    // filter status
    //---------------
    filterStatus() {
      // console.log("---------------------------");
      // console.log("IN filterStatus");
      //------------------------
      // get filter status desc
      //------------------------
      let filterStatusDesc = getFilterStatusDesc(this.filterStatus)
      //-----------------------------
      // filter combatants by status
      //-----------------------------
      this.filterCombatantStatuses(filterStatusDesc)
      //----------------------
      // check show/hide cols
      //----------------------
      if (this.toggleDesc) {
        this.showSummaryHeadings()
      } else {
        this.showDetailHeadings()
      }
      this.refreshCombatActionReactionCols(filterStatusDesc)
    },
    //========================================================
    // watch toggle numbers
    //----------------------
    watch_toggleNumbers() {
      //------------------
      // show number cols
      //------------------
      if (this.toggleNumbers) {
        this.showNumberCols()
        //------------
        // reset cols
        //------------
      } else if (this.toggleDesc) {
        this.showSummaryHeadings()
      } else {
        this.showDetailHeadings()
      }
    },
    //========================================================
    // watch toggle extras
    //---------------------
    watch_toggleExtras() {
      console.log('---------------------------')
      console.log(
        'IN watch_toggleExtras: 1. arena_extra_ready = ' +
          this.arena_extra_ready,
        '2. arena_in_progress = ' + this.arena_in_progress,
        '3. toggleExtras = ' + this.toggleExtras,
        '4. unused_action_count = ' + this.unused_action_count,
      )
      //---------------------------------------
      // where combat i/p (i.e. haven't ended)
      //---------------------------------------
      if (this.arena_in_progress) {
        console.log('6. arena_extra_ready = true')
        //-----------------
        // show extra cols
        //-----------------
        if (this.toggleExtras) {
          console.log('7. toggleExtras = true')

          // tw change >>
          // set view round as could be on previous/future round when extra attacks button pressed
          // console.log('8. setting view round to arena_round = ' + this.arena_round)
          // setViewRound(this.arena_round)
          // tw change <<

          this.showExtraCols()
          this.arena_extra_ready = false
          this.unused_action_count = getUnusedExtraActions(this.combatants)
          //-------------------------
          // set unused action count
          //-------------------------
          this.setUnusedActionCount(this.unused_action_count)
          //-------------------
          // update scoreboard
          //-------------------
          this.emitter.emit('update-scoreboard')
        }
      } else {
        console.log('9. arena_in_progress = false')
      }
    },
    //========================================================
    // watch view round
    //------------------
    async watch_view_round() {
      console.log('---------------------------')
      console.log('IN watch_view_round')
      //-------------
      // set unready
      //-------------
      this.loadReady = false
      //-----------------------------------
      // change round based upon direction
      //-----------------------------------
      this.view_round = this.getViewRound()
      //-----------------------------------
      // A. where cur round NOT view round
      //-----------------------------------
      if (this.view_round != this.arena_round) {
        if (this.selected_reaction) {
          this.emitter.emit('reset-panel-reactions', this.selected_reaction)
          this.selected_reaction = null
        }
        //---------------------------------------------
        // where not already saved - save extra action
        //---------------------------------------------
        if (this.arena_extra_action > 0 && this.arena_extra_action_save == 0) {
          this.arena_extra_action_save = this.arena_extra_action
          //----------------------------
          // clear current extra action
          //----------------------------
          this.arena_extra_action = 0
        }
        //-------------------------------------
        // B. where current round = view round
        //-------------------------------------
      } else {
        //--------------------------
        // where saved extra action
        //--------------------------
        if (this.arena_extra_action_save > 0) {
          //-----------------------------
          // restore extra action number
          //-----------------------------
          this.arena_extra_action = this.arena_extra_action_save
          //-------------------------
          // clear extra action save
          //-------------------------
          this.arena_extra_action_save = 0
        }
      }
      //-----------------------------
      // where combatants are loaded
      //-----------------------------
      if (this.combatants.length > 0) {
        //----------------------------------------
        // load round slots/images for view round
        //----------------------------------------
        await this.loadArenaRoundSlotsHandler(this.view_round)
        await this.loadPreviousRoundActionImages(this.view_round)
        //-------------------
        // extra attack cols      (redisplayed again if view next round and return to extra attacks mode)
        //-------------------
        if (this.toggleExtras && this.arena_round == this.view_round) {
          //-------------------------
          // show extra cols (again)
          //-------------------------
          this.showExtraCols()
          //----------------------------
          // normal summary/detail cols
          //----------------------------
        } else if (this.toggleDesc) {
          this.showSummaryHeadings()
        } else {
          this.showDetailHeadings()
        }
      }
      //-------------------
      // update scoreboard
      //-------------------
      this.emitter.emit('update-scoreboard')
      //--------------------------------------------------------------
      // keep loader visible while new combat initialisation is still
      // clearing/resetting rounds before combatants have been deployed
      //--------------------------------------------------------------
      if (this.isInitialisingCombat && this.combatants.length === 0) {
        return
      }
      //-----------
      // set ready
      //-----------
      this.loadReady = true
      //-------------------
      // extra attack cols
      //-------------------
      // // tw change >>
      // if (this.toggleExtras && this.arena_round == this.view_round) {
      //   //--------------------------
      //   // redisplay extra attacks
      //   //-------------------------
      //   this.redisplayExtraAttacks()
      //   // tw change <<
      //   //-------------
      //   // normal cols
      //   //-------------
      // } else {
      //   if (this.toggleDesc) {
      //     this.showSummaryHeadings()
      //   } else {
      //     this.showDetailHeadings()
      //   }
      // }
    },
    //========================================================
    // watch arena round
    //------------------
    watch_arena_round() {
      if (this.arena_round > 1) {
        //-----------------------------
        // where combatants are loaded
        //-----------------------------
        if (this.combatants.length > 0) {
          this.loadReady = false
          //------------------------
          // load campaign round slots
          //------------------------
          this.loadArenaRoundSlotsHandler(this.view_round).then(async () => {
            await this.loadPreviousRoundActionImages(this.view_round)
            this.loadReady = true
            //-------------------
            // update scoreboard
            //-------------------
            this.emitter.emit('update-scoreboard')
          })
        }
      }
    },
  },
  //========================================================
  // computed
  //----------
  computed: {
    showSkillsViewer() {
      return this.$route.query.popup === 'party-skills'
    },
    showPartyRunesViewer() {
      return this.$route.query.popup === 'party-runes'
    },
    showLogPanel() {
      return this.$route.query.popup === 'log'
    },
    gotCombatants() {
      if (this.combatants && this.combatants.length > 0) {
        return true
      } else {
        return false
      }
    },
    minBleedCount() {
      return this.config.min_bleed
    },
    maxBleedCount() {
      return this.config.max_bleed
    },
    stored_cur_action() {
      return this.getCurrentActionNumber()
    },
    outstanding_rolls() {
      if (
        this.filterStatus &&
        this.arena_rolled &&
        hasOutstandingSRRolls(this.combatants)
      ) {
        return true
      } else {
        return false
      }
    },
    missing_check() {
      //----------------------------
      // where:
      // - have id
      // - is not first action
      // - combat is in progress
      // - not in extra attack mode
      //----------------------------
      if (
        this.stored_cur_action > 1 &&
        this.arena_in_progress &&
        !this.arena_extra_ready &&
        this.arena_extra_action == 0
      ) {
        return true
      } else {
        return false
      }
    },
    race_count() {
      return this.races.length
    },
    arenaSubStatus() {
      return getArenaSubStatus({
        isInitialisingCombat: this.isInitialisingCombat,
        arena_status: this.arena_status,
        arena_ended: this.arena_ended,
        toggleNumbers: this.toggleNumbers,
        awaitingRollOffs: this.toggleNumbers && this.dupes.length > 1,
        arena_rolled: this.arena_rolled,
        arena_extra_ready: this.arena_extra_ready,
        arena_extra_action: this.arena_extra_action,
        arena_bleeders_ready: this.arena_bleeders_ready,
        arena_round_completed: this.arena_round_completed,
      })
    },
    toggleActions() {
      return canEditViewedRound({
        view_round: this.view_round,
        arena_round: this.arena_round,
        arena_extra_ready: this.arena_extra_ready,
        arena_extra_action: this.arena_extra_action,
        arena_round_completed: this.arena_round_completed,
      })
    },
    tableOverlayDisabled() {
      const isNextRound = this.view_round == this.arena_round + 1

      if (this.view_round < this.arena_round) {
        return true
      }

      if (
        this.arena_extra_ready &&
        this.view_round != this.arena_round &&
        !isNextRound
      ) {
        return true
      }

      if (
        this.arena_extra_action > 0 &&
        this.view_round != this.arena_round &&
        !isNextRound
      ) {
        return true
      }

      return false
    },
    ca_extra() {
      return this.maxCA + 1
    },
    arena_active_combatants() {
      return this.getActiveCombatantCount()
    },
    arena_inactive_combatants() {
      return this.getInactiveCombatantCount()
    },
    watch_toggleNumbers() {
      return this.toggleNumbers
    },
    watch_toggleExtras() {
      return this.toggleExtras
    },
    watch_loadReady() {
      return this.loadReady
    },
    watch_view_round() {
      return this.getViewRound()
    },
    watch_arena_round() {
      return this.arena_round
    },
    selected_action_ind() {
      return this.selected_action != null
    },
    selected_reaction_ind() {
      return this.selected_reaction != null
    },
    showMarkersPanel() {
      return shouldShowMarkersPanel({
        gotCombatants: this.gotCombatants,
        loadActionsReady: this.loadActionsReady,
        toggleNumbers: this.toggleNumbers,
        arena_extra_action_ready: this.arena_extra_action_ready,
        arena_extra_action: this.arena_extra_action,
        arena_round_completed: this.arena_round_completed,
        arena_extra_ready: this.arena_extra_ready,
      })
    },
    areMarkersDisabled() {
      return shouldDisableMarkersPanel({
        arena_in_progress: this.arena_in_progress,
        arena_rolled: this.arena_rolled,
        arena_extra_ready: this.arena_extra_ready,
      })
    },
    combatantNames() {
      const names_array = this.combatants.map(function (array) {
        return array.name
      })
      return names_array
    },
    isInvertedSidePanelOpen() {
      return !this.isSidePanelOpen
    },
    api_request() {
      return window.bbAPIRequest(this.endpoint, this.local_slug, this.deleteInd)
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //--------------------
    // ref mapped methods
    //--------------------
    ...mapMutations('auth', {
      setCurrentView: 'setCurrentView',
      setMessage: 'setMessage',
      destroyToken: 'destroyToken',
      destroyUserProfile: 'destroyUserProfile',
    }),
    ...mapMutations('combat', {
      setPlayerNames: 'setPlayerNames',
      setArenaSubStatus: 'setArenaSubStatus',
      setUnusedActionCount: 'setUnusedActionCount',
      setCurrentActionNumber: 'setCurrentActionNumber',
    }),
    ...mapGetters('combat', {
      getInactiveCombatantCount: 'getInactiveCombatantCount',
      getActiveCombatantCount: 'getActiveCombatantCount',
      getCurrentActionNumber: 'getCurrentActionNumber',
      getUnusedActionCount: 'getUnusedActionCount',
      getCampaignPlayers: 'getCampaignPlayers',
      getCombatants: 'getCombatants',
      getViewRound: 'getViewRound',
      getReactions: 'getReactions',
      getActions: 'getActions',
      getMarkers: 'getMarkers',
      getConfig: 'getConfig',
      getCampaign: 'getCampaign',
    }),
    ...mapGetters('races', {
      getRaces: 'getRaces',
    }),
    ...mapGetters('characters', {
      getCharacters: 'getCharacters',
    }),
    ...mapActions('combat', {
      loadStaticVisibilities: 'loadStaticVisibilities',
      loadStaticReactions: 'loadStaticReactions',
      loadStaticStatuses: 'loadStaticStatuses',
      loadStaticFatigues: 'loadStaticFatigues',
      loadStaticActions: 'loadStaticActions',
      loadStaticMarkers: 'loadStaticMarkers',
      loadStaticOptions: 'loadStaticOptions',
      loadCampaignPlayers: 'loadCampaignPlayers',
      loadStaticRoles: 'loadStaticRoles',
      loadConfig: 'loadConfig',
      loadCampaign: 'loadCampaign',

      changeCombatantImage: 'changeCombatantImage',
      deleteCombatant: 'deleteCombatant',
      editCombatant: 'editCombatant',
      clearDebugLog: 'clearDebugLog',
      clearArenaLog: 'clearArenaLog',
    }),
    ...mapActions('races', {
      loadStaticRaces: 'loadStaticRaces',
    }),
    //
    //************************************
    //
    //      SCREEN/TABLE MANIPULATION
    //
    //************************************
    //
    //=====================================================================
    // confirm event
    //---------------
    async confirmEvent(event) {
      // console.log("----------------------");
      // console.log("IN confirmEvent = " + event);
      //-----------------------------
      // set ok/cancel button labels
      //-----------------------------
      let cancel_label
      let ok_label
      if (this.isRetainStrikeRanksEvent(event)) {
        ok_label = 'Yes'
        cancel_label = 'No'
      } else {
        ok_label = 'OK'
        cancel_label = 'Cancel'
      }
      //-------------------
      // call confirmation
      //-------------------
      if (this.$refs.confirmDialog) {
        const ok = await this.$refs.confirmDialog.show({
          title: '',
          message: 'Confirm',
          subject: event,
          okButton: ok_label,
          cancelButton: cancel_label,
          contentClass: 'bb-combat-confirm-dialog',
        })
        //-----------
        // confirmed
        //-----------
        if (ok) {
          if (event == 'Unlock Campaign') {
            this.lockArenaHarness(false)
            //
          } else if (event == 'Lock Campaign') {
            this.lockArenaHarness(true)
            //
          } else if (this.isRetainStrikeRanksEvent(event)) {
            this.newRoundHarness()
            //
          } else if (event == 'Extra Attacks') {
            this.toggleExtras = true
            //
          } else if (event == 'New Round') {
            this.checkRetainSRRolls()
            //
          } else if (event == 'Start Combat') {
            this.startCombatHarness()
            //
          } else if (event == 'End Combat') {
            let message = 'Combat Ended'
            this.initCombatHarness(false, message)
            //
          } else if (event == 'New Combat') {
            let message = 'Combat ready'
            this.initCombatHarness(true, message)
          }
          //--------
          // cancel
          //--------
        } else if (this.isRetainStrikeRanksEvent(event)) {
          //-----------------
          // ready for rolls
          //-----------------
          this.arena_rolled = false
          //-----------------
          // prompt sr rolls
          //-----------------
          this.manageRolls()
        }
      }
    },
    //=====================================================================
    // init combat harness
    //---------------------
    async initCombatHarness(new_combat, message) {
      // console.log("-------------------");
      // console.log("IN initCombatHarness");
      let this_ended = false
      this.loadReady = false
      this.isInitialisingCombat = new_combat
      //---------------
      // reset columns
      //---------------
      this.curActiveColInds = null
      this.toggleNumbers = false
      this.toggleExtras = false
      //--------------------
      // show detailed cols
      //--------------------
      this.showDetailHeadings()
      //-----------------
      // clear arena log
      //-----------------
      this.clearArenaLogHarness()
      //----------------
      // return promise
      //----------------
      return new Promise(() => {
        //---------------------------
        // clear existing combatants
        //---------------------------
        let call = useClearCampaignCombatants(this.arena_slug)
        call
          .then(() => {
            //------------------------
            // clear state combatants
            //------------------------
            clearCampaignCombatants()
            //------------------------
            // clear local combatants
            //------------------------
            this.combatants.splice(0)
            //-----------------------------------------------
            // get auto-deployable characters if new combat
            //----------------------------------------------
            if (new_combat) {
              this_ended = false
              this.loadCampaignDeployableCharactersHarness(message)
            } else {
              //------------------
              // set combat ended
              //------------------
              this_ended = true
              this.isInitialisingCombat = false
              this.loadReady = true
            }
            //---------------------
            // set progress values
            //---------------------
            this.arena_status = 'Inactive'
            //
            this.view_round = 0
            this.arena_round = 0
            this.arena_action = 0
            this.lastLoggableActionNumber = 0
            this.arena_extra_action = 0
            this.unused_action_count = 0
            this.setCurrentActionNumber(0)
            //
            this.arena_rolled = false
            this.arena_in_progress = false
            this.arena_extra_ready = false
            this.arena_bleeders_ready = false
            this.arena_bleeders_shown = false
            this.arena_round_completed = false
            //
            this.arena_ended = this_ended
            //----------------
            // set view round
            //----------------
            setViewRound(this.view_round)
            //-------------------------
            // set unused action count
            //-------------------------
            this.setUnusedActionCount(this.unused_action_count)
            //-------------------
            // set progress parm
            //-------------------
            let progress_parm = {
              slug: this.arena.slug,
              payload: {
                status: this.arena_status,
                round: this.arena_round,
                action: this.arena_action,
                rolled: this.arena_rolled,
                in_progress: this.arena_in_progress,
                round_completed: this.arena_round_completed,
                locked: this.arena_locked,
                ended: this.arena_ended,
              },
            }
            //-----------------------
            // update arena progress
            //-----------------------
            this.useUpdateArenaProgressHarness(
              progress_parm,
              new_combat ? '' : message,
            )
          })
          .catch((err) => {
            this.isInitialisingCombat = false
            handleError(err, 23)
          })
      })
    },
    //=====================================================================
    // start combat harness
    //----------------------
    startCombatHarness() {
      console.log('-----------------------')
      console.log('startCombatHarness')
      //-------------------
      // set active status
      //-------------------
      this.arena_status = 'Active'
      this.showCombatTextOverlay('Combat Started')
      //-----------
      // new round
      //-----------
      this.newRoundHarness()
      //-------------------
      // update scoreboard
      //-------------------
      this.emitter.emit('update-scoreboard')
    },
    //=====================================================================
    // check all sr rolled
    //---------------------
    checkAllSRRolled() {
      console.log('-------------------')
      console.log('IN checkAllSRRolled')
      // console.log("this.arena_rolled = " + this.arena_rolled);
      //----------------------------------------------------------------------
      // check ALL srs rolled if already flagged (catch newly deployed chars)
      //----------------------------------------------------------------------
      if (this.arena_rolled) {
        let zero_srs = this.combatants.some(
          (c) => c.status == 'Active' && c.sr_roll == 0,
        )
        if (zero_srs) {
          // console.log("got some zero srs - reset flag...");
          //-----------
          // get rolls
          //-----------
          this.manageRolls()
        }
      }
    },
    //=====================================================================
    // manage missing actions
    //------------------------
    manageMissingActions(ids = null) {
      console.log('--------------------------')
      console.log('IN manageMissingActions')
      let check_ids = []
      //-----------------------------------------------
      // where new active ids NOT known (i.e. deploys)
      //-----------------------------------------------
      if (!ids) {
        //--------------------
        // get all active ids
        //--------------------
        check_ids = this.combatants
          .filter((c) => c.status == 'Active')
          .map((m) => m.id)
        //---------------------
        // else use passed ids (i.e. add combs, i>a)
        //---------------------
      } else {
        check_ids = ids
      }
      //-------------------------------------------------------------
      // loop ids - getting local combatant to check missing actions
      //-------------------------------------------------------------
      check_ids.forEach((id) => {
        let combatant = this.combatants.find((x) => x.id == id)

        if (combatant) {
          this.manageMissingActionsSpecificCombatant(combatant)
        }
      })
    },
    //=====================================================================
    // manage missing actions for specific combatant
    //-----------------------------------------------
    manageMissingActionsSpecificCombatant(combatant) {
      console.log('--------------------------')
      console.log('IN manageMissingActionsSpecificCombatant')
      let cur_action = this.stored_cur_action
      // *** catch newly deployed chars/re-activated combatants ***
      //-------------------------------------
      // check missing actions for combatant
      //-------------------------------------
      let missing_cas = hasMissingCombatantActions(combatant, cur_action)
      //------------------------------------------
      // where missing create "no-action" actions
      //------------------------------------------
      if (missing_cas.length > 0) {
        let no_action = getAction('no_action')
        missing_cas.forEach((a) => {
          let slot = {
            id: combatant.id,
            slot: '',
            slot_name: 'pending',
            slot_slug: '',
            slot_type: '',
            slot_num: a,
          }
          this.prepActionCall(slot, no_action)
        })
      }
    },
    shouldAutoStartNewRound() {
      return this.refreshCombatConfig().auto_start_new_round === true
    },
    shouldUseCombatTextAnimations() {
      const config = this.refreshCombatConfig()

      return config.animation === true || config.animations === true
    },
    isRetainStrikeRanksEvent(event = '') {
      return String(event).startsWith('Retain Strike Ranks')
    },
    refreshCombatConfig() {
      const nextConfig = this.getConfig()

      if (nextConfig) {
        this.config = nextConfig
      }

      return this.config || {}
    },
    triggerNextRound() {
      if (this.shouldAutoStartNewRound()) {
        this.checkRetainSRRolls()
      } else {
        this.confirmEvent('New Round')
      }
    },
    //=====================================================================
    // check retain strike ranks
    //---------------------------
    checkRetainSRRolls() {
      // console.log("-------------------");
      // console.log("IN checkRetainSRRolls");
      const config = this.refreshCombatConfig()
      //-------------------------------------
      // new round if not retaining sr rolls
      //-------------------------------------
      if (config.prompt_keep_sr_rolls) {
        this.confirmEvent('Retain Strike Ranks')
        //-----------------
        // prompt sr rolls
        //-----------------
      } else {
        //-----------------
        // ready for rolls
        //-----------------
        this.arena_rolled = false
        this.manageRolls()
      }
    },
    //=====================================================================
    // update rolled status
    //----------------------
    updateRolledStatus() {
      console.log('------------------')
      console.log('IN updateRolledStatus')
      //---------------
      // set as rolled
      //----------------
      this.arena_rolled = true
      let message
      //-------------
      // set message
      //-------------
      message = 'Start combat'
      //-------------------
      // set progress parm
      //-------------------
      let progress_parm = {
        slug: this.arena.slug,
        payload: {
          rolled: this.arena_rolled,
          in_progress: this.arena_in_progress,
        },
      }
      //-----------------------
      // update arena progress
      //-----------------------
      this.useUpdateArenaProgressHarness(progress_parm, message)
    },
    //=====================================================================
    // new round harness
    //-------------------
    newRoundHarness() {
      console.log('------------------')
      console.log('IN newRoundHarness')
      const wasCombatInProgress = this.arena_in_progress
      //-----------------------
      // switch off extra cols
      //-----------------------
      if (this.toggleExtras) {
        this.toggleExtras = !this.toggleExtras
      }
      //--------------------------
      // switch off bleeders view
      //--------------------------
      if (this.arena_bleeders_shown) {
        this.filterCombatantStatuses('Active')
        this.arena_bleeders_shown = false
      }
      //--------------------------
      // filter active combatants
      //--------------------------
      this.filterCombatantStatuses('Active')
      //------------------------
      // force summary headings
      //------------------------
      this.showSummaryHeadings()
      //---------------------
      // increment new round
      //---------------------
      let progress = newRound()
      if (wasCombatInProgress) {
        this.showCombatTextOverlay('New Round')
      }
      //-------------------------
      // set unused action count
      //-------------------------
      this.setUnusedActionCount(this.arena_active_combatants)
      //
      this.arena_round = progress.round
      this.arena_extra_action = 0
      this.arena_action = 1
      this.captureLoggableActionNumber(this.arena_action)
      //
      this.setCurrentActionNumber(this.arena_action)
      //
      this.arena_round_completed = false
      this.arena_extra_ready = false
      this.arena_bleeders_ready = false
      this.arena_bleeders_shown = false
      this.arena_in_progress = true
      //
      this.view_round = this.arena_round
      setViewRound(this.view_round)
      //-------------
      // set message
      //-------------
      let message = 'Round ' + this.arena_round + ' started'
      //-------------------
      // set progress parm
      //-------------------
      let progress_parm = {
        slug: this.arena.slug,
        payload: {
          status: this.arena_status,
          round: this.arena_round,
          action: this.arena_action,
          in_progress: this.arena_in_progress,
          round_completed: this.arena_round_completed,
          ended: this.arena_ended,
        },
      }
      //-----------------------
      // update arena progress
      //-----------------------
      this.useUpdateArenaProgressHarness(progress_parm, message, {
        combatMessage: {
          message: 'Action 1 started',
          actionNumber: this.arena_action,
        },
      })
    },
    showCombatTextOverlay(message = '') {
      if (!message || !this.shouldUseCombatTextAnimations()) {
        return
      }

      if (this.combatTextOverlayTimer) {
        clearTimeout(this.combatTextOverlayTimer)
        this.combatTextOverlayTimer = null
      }

      this.combatTextOverlayVisible = false
      this.combatTextOverlayMessage = message
      this.combatTextOverlayKey++

      window.requestAnimationFrame(() => {
        this.combatTextOverlayVisible = true
      })

      this.combatTextOverlayTimer = window.setTimeout(() => {
        this.combatTextOverlayVisible = false
        this.combatTextOverlayTimer = null
      }, 1500)
    },
    //=====================================================================
    // new action harness
    //--------------------
    newActionHarness(deleted_slot = false) {
      console.log('------------------')
      console.log('IN newActionHarness')
      let message
      //-------------
      // set message
      //-------------
      if (!deleted_slot) {
        message = 'Action ' + this.arena_action + ' started'
      } else {
        message = 'Action ' + this.arena_action + ' revisited'
      }
      //-------------------
      // set progress parm
      //-------------------
      let progress_parm = {
        slug: this.arena.slug,
        payload: {
          action: this.arena_action,
        },
      }
      //-----------------------
      // update arena progress
      //-----------------------
      this.captureLoggableActionNumber(this.arena_action)
      this.useUpdateArenaProgressHarness(progress_parm, '', {
        combatMessage: {
          message,
          actionNumber: this.arena_action,
        },
      })
    },
    //=====================================================================
    // new action harness
    //--------------------
    lockArenaHarness(toggle) {
      console.log('------------------')
      console.log('IN lockArenaHarness = ' + toggle)
      let message
      //-------------
      // set message
      //-------------
      if (toggle) {
        message = 'Arena locked'
      } else {
        message = 'Arena unlocked'
      }
      //-------------
      // toggle lock
      //-------------
      this.arena_locked = !this.arena_locked
      //-------------------
      // set progress parm
      //-------------------
      let progress_parm = {
        slug: this.arena.slug,
        payload: {
          locked: toggle,
        },
      }
      //-----------------------
      // update arena progress
      //-----------------------
      this.useUpdateArenaProgressHarness(progress_parm, message)
    },
    //=====================================================================
    // manage clear surprises
    //------------------------
    manageClearSurprises() {
      console.log('--------------------------------------')
      console.log('IN manageClearSurprises')
      let parm_array = []
      //-------------------
      // get any surprises
      //-------------------
      let surprised = getSurprised(this.combatants)
      //-----------------
      // where surprised
      //-----------------
      if (surprised.length > 0) {
        // console.log("surprised count = " + surprised.length);
        //----------
        // set parms
        //-----------
        surprised.forEach((s) => {
          // let parm = {
          //   payload: {
          //     name: s.name,
          //     slug: s.slug,
          //     surprised: false,
          //   },
          // };
          let payload = {
            name: s.name,
            slug: s.slug,
            surprised: false,
          }
          parm_array.push(payload)
        })
        //-----------------
        // clear surprises
        //-----------------
        if (surprised) {
          this.updateMultipleCombatantsHarness(
            parm_array,
            'no longer surprised.',
          )
        }
      }
    },
    //=====================================================================
    // end round harness
    //-------------------
    endRoundHarness() {
      console.log('------------------')
      console.log('IN endRoundHarness')
      //------------
      // set values
      //------------
      this.arena_extra_action = 0
      this.arena_round_completed = true
      //-----------------
      // clear surprises
      //-----------------
      this.manageClearSurprises()
      //-------------------
      // set progress parm
      //-------------------
      let progress_parm = {
        slug: this.arena.slug,
        payload: {
          in_progress: this.arena_in_progress,
          round_completed: this.arena_round_completed,
        },
      }
      //-----------------------
      // update arena progress
      //-----------------------
      this.useUpdateArenaProgressHarness(progress_parm)
    },
    //========================================================
    // load actions cols
    //-------------------
    loadActionCols() {
      // console.log("-------------------");
      // console.log("IN loadActionCols");
      //--------------------------
      // find insertion anchor field index
      //--------------------------
      this.new_col = this.headings.findIndex((x) => x.field == 'fill_anchor')

      let filler_before_last_action = {
        id: this.new_col,
        label: '',
        field: 'fill_last_action_before',
        filterable: false,
        sortable: false,
        width: '1px',
        tdClass: 'bb-is-fill-wide',
        thClass: 'bb-is-fill-wide',
        hidden: true,
      }
      this.headings.splice(this.new_col, 0, filler_before_last_action)
      this.new_col++

      let last_action = {
        id: this.new_col,
        label: 'Last Action',
        field: 'prev_round_action_image',
        tdClass: 'text-center bb-combat-image-cell',
        filterable: false,
        sortable: false,
        width: '90px',
        hidden: true,
      }
      this.headings.splice(this.new_col, 0, last_action)
      this.new_col++

      let filler_after_last_action = {
        id: this.new_col,
        label: '',
        field: 'fill_last_action_after',
        filterable: false,
        sortable: false,
        width: '1px',
        tdClass: 'bb-is-fill-wide',
        thClass: 'bb-is-fill-wide',
        hidden: true,
      }
      this.headings.splice(this.new_col, 0, filler_after_last_action)
      this.new_col++

      let action_num = 1
      //------------------
      // loop to ca-extra
      //------------------
      for (let i = 0; i < this.ca_extra; i++) {
        //------------------------------------------
        // set ca (image) col as normal ca or extra
        //------------------------------------------
        let ca_label = ''
        let ca_hidden = false
        if (action_num == this.ca_extra) {
          const shield_idx = this.headings.findIndex((x) => x.field == 'shield')
          let shield =
            shield_idx > -1
              ? this.headings.splice(shield_idx, 1)[0]
              : {
                  id: this.new_col,
                  label: 'Shield',
                  field: 'shield',
                  tdClass: 'text-center bb-combat-image-cell',
                  hidden: true,
                }
          shield.filterable = false
          shield.sortable = false
          shield.width = '70px'
          shield.hidden = true
          this.headings.splice(this.new_col, 0, shield)
          this.new_col++

          const second_weapon_idx = this.headings.findIndex(
            (x) => x.field == 'second_weapon',
          )
          let second_weapon =
            second_weapon_idx > -1
              ? this.headings.splice(second_weapon_idx, 1)[0]
              : {
                  id: this.new_col,
                  label: 'Second-Weapon',
                  field: 'second_weapon',
                  tdClass: 'text-center bb-combat-image-cell',
                  hidden: true,
                }
          second_weapon.label = 'Second Weapon'
          second_weapon.filterable = false
          second_weapon.sortable = false
          second_weapon.width = '90px'
          second_weapon.hidden = true
          this.headings.splice(this.new_col, 0, second_weapon)
          this.new_col++

          let extra_attack_options = {
            id: this.new_col,
            label: 'Off-Hand',
            field: 'extra_attack_options',
            tdClass: 'text-center bb-combat-image-cell',
            filterable: false,
            sortable: false,
            width: '120px',
            hidden: true,
          }
          this.headings.splice(this.new_col, 0, extra_attack_options)
          this.new_col++

          let last_action = {
            id: this.new_col,
            label: 'Last Action',
            field: 'last_action_image',
            tdClass: 'text-center bb-combat-image-cell',
            filterable: false,
            sortable: false,
            width: '90px',
            hidden: true,
          }
          this.headings.splice(this.new_col, 0, last_action)
          this.new_col++

          let next_action = {
            id: this.new_col,
            label: 'Next Action',
            field: 'next_action_image',
            tdClass: 'text-center bb-combat-image-cell',
            filterable: false,
            sortable: false,
            width: '90px',
            hidden: true,
          }
          this.headings.splice(this.new_col, 0, next_action)
          this.new_col++

          let no_attack_reason = {
            id: this.new_col,
            label: 'No Extra Attack Reason',
            field: 'no_attack_reason',
            tdClass: 'text-center',
            filterable: false,
            sortable: false,
            width: '240px',
            hidden: true,
          }
          this.headings.splice(this.new_col, 0, no_attack_reason)
          this.new_col++

          let filler_before_extra = {
            id: this.new_col,
            label: '',
            field: 'fill_extra_before',
            filterable: false,
            sortable: false,
            width: '1px',
            tdClass: 'bb-is-fill-wide',
            thClass: 'bb-is-fill-wide',
          }
          this.headings.splice(this.new_col, 0, filler_before_extra)
          this.new_col++
        }
        if (action_num == this.ca_extra) {
          ca_label = 'Extra'
          ca_hidden = false // <------- XXXX was true
        } else {
          ca_label = 'CA' + action_num
          ca_hidden = false
        }
        //============================================
        // set new action image col
        //--------------------------
        let action = {
          id: this.new_col,
          label: ca_label,
          field: 'ca' + action_num,
          tdClass: 'text-center bb-combat-image-cell',
          filterable: false,
          sortable: false,
          width: '90px',
          hidden: ca_hidden,
        }
        //-------------
        // add new col
        //-------------
        this.headings.splice(this.new_col, 0, action)
        //-------------------
        // increment col num
        //-------------------
        this.new_col++
        //============================================
        // set new action name col
        //-------------------------
        let action_name = {
          id: this.new_col,
          label: 'CA Name' + action_num,
          field: 'ca_name' + action_num,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        //-------------
        // add new col
        //-------------
        this.headings.splice(this.new_col, 0, action_name)
        //-------------------
        // increment col num
        //-------------------
        this.new_col++
        //============================================
        // set new action slug col
        //-------------------------
        let action_slug = {
          id: this.new_col,
          label: 'CA Slug' + action_num,
          field: 'ca_slug' + action_num,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        //-------------
        // add new col
        //-------------
        this.headings.splice(this.new_col, 0, action_slug)
        //-------------------
        // increment col num
        //-------------------
        this.new_col++
        //============================================
        // set new action type col
        //-------------------------
        let action_type = {
          id: this.new_col,
          label: 'CA Type' + action_num,
          field: 'ca_type' + action_num,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        //-------------
        // add new col
        //-------------
        this.headings.splice(this.new_col, 0, action_type)
        //-------------------
        // increment col num
        //-------------------
        this.new_col++
        //----------------------
        // increment action num
        //----------------------
        action_num++
      }
      //============================================
      // set filler col after extra
      //----------------------------
      let filler = {
        id: this.new_col,
        label: '',
        field: 'fill2',
        filterable: false,
        sortable: false,
        width: '1px',
        tdClass: 'bb-is-fill-wide',
        thClass: 'bb-is-fill-wide',
      }
      this.headings.splice(this.new_col, 0, filler)
      this.new_col++
    },
    //========================================================
    // load reaction cols
    //--------------------
    loadReactionCols() {
      //-----------
      // set vars
      //-----------
      let reaction_num = this.config.min_cr
      //----------------
      // loop to max cr
      //----------------
      for (let i = 0; i < this.maxCR; i++) {
        //============================================
        // set new reaction image col
        //----------------------------
        let reaction = {
          id: this.new_col,
          label: 'CR' + reaction_num,
          field: 'cr' + reaction_num,
          tdClass: 'text-center bb-combat-image-cell',
          filterable: false,
          sortable: false,
          width: '90px',
        }
        //-------------
        // add new col
        //-------------
        this.headings.splice(this.new_col, 0, reaction)
        this.new_col++
        //============================================
        // set new reaction name col
        //----------------------------
        let reaction_name = {
          id: this.new_col,
          label: 'CR Name' + reaction_num,
          field: 'cr_name' + reaction_num,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        //-------------
        // add new col
        //-------------
        this.headings.splice(this.new_col, 0, reaction_name)
        this.new_col++
        //============================================
        // set new reaction slug col
        //---------------------------
        let reaction_slug = {
          id: this.new_col,
          label: 'CR Slug' + reaction_num,
          field: 'cr_slug' + reaction_num,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        //-------------
        // add new col
        //-------------
        this.headings.splice(this.new_col, 0, reaction_slug)
        this.new_col++
        //------------------------
        // increment reaction num
        //------------------------
        reaction_num++
      }
      //============================================
      // set filler col
      //----------------
      let filler2 = {
        id: this.new_col,
        label: '',
        field: 'fill3',
        filterable: false,
        sortable: false,
        width: '1px',
        tdClass: 'bb-is-fill',
      }
      //-------------
      // add new col
      //-------------
      this.headings.splice(this.new_col, 0, filler2)
      this.new_col++
    },
    //========================================================
    // refresh combat action/reaction cols
    //------------------------------------
    refreshCombatActionReactionCols(status = null) {
      if (!this.config) {
        return
      }

      const filterStatusDesc = status ?? getFilterStatusDesc(this.filterStatus)

      this.toggleActionCols(filterStatusDesc)
      this.toggleReactionCols(filterStatusDesc)
    },
    //========================================================
    // toggle actions cols
    //---------------------
    toggleActionCols(status) {
      // console.log("-------------------");
      // console.log("IN toggleActionCols");
      let action_num = 0
      const base_hidden_status = status != 'Active'
      //------------------
      // loop to ca extra
      //------------------
      for (let i = 0; i < this.ca_extra; i++) {
        action_num = i + 1
        let slot
        slot = 'ca' + action_num
        //----------------------
        // find col field index
        //----------------------
        let col_idx = this.headings.findIndex((x) => x.field == slot)
        let hidden_status = base_hidden_status
        //-------------------------------------------
        // keep ca-extra hidden unless in extra mode
        //-------------------------------------------
        if (action_num == this.ca_extra && this.arena_extra_action == 0) {
          hidden_status = true
        }
        if (col_idx < 0) {
          continue
        }
        //---------------
        // show/hide col
        //---------------
        this.headings[col_idx].hidden = hidden_status
      }
    },
    //========================================================
    // toggle reactions cols
    //-----------------------
    toggleReactionCols(status) {
      let reaction_num = 0
      const hidden_status = status != 'Active'
      //----------------
      // loop to max cr
      //----------------
      for (let i = 0; i < this.maxCR; i++) {
        reaction_num = i + 1
        //----------------------------
        // set col field to show/hide
        //----------------------------
        let slot = 'cr' + reaction_num
        //----------------------
        // find col field index
        //----------------------
        let col_idx = Array.prototype.findIndex.call(
          this.headings,
          (x) => x.field == slot,
        )
        if (col_idx < 0) {
          continue
        }
        //---------------
        // show/hide col
        //---------------
        this.headings[col_idx].hidden = hidden_status
      }
    },
    //========================================================
    // toggle desc headings
    //----------------------
    toggleDescHeadings() {
      // console.log("-------------------------");
      // console.log("IN toggleDescHeadings");
      if (this.toggleDesc) {
        this.showDetailHeadings()
      } else {
        this.showSummaryHeadings()
      }
    },
    //========================================================
    // show detailed headings
    //------------------------
    showDetailHeadings() {
      // console.log("-------------------------");
      // console.log("IN showDetailHeadings");
      let i = 0
      //-----------------------------
      // get desc detail col numbers
      //-----------------------------
      let colNumbers = getDescDetailCols({
        headings: this.headings,
        filter_status: this.filterStatus,
        view_round: this.view_round,
        cur_round: this.arena_round,
      })
      //---------------
      // hide all cols
      //---------------
      this.headings.map((a) => (a.hidden = true))
      //--------------------
      // show detailed cols
      //--------------------
      this.toggleDesc = false
      //--------------------
      // show detailed cols
      //--------------------
      while (i < colNumbers.length) {
        this.headings[colNumbers[i]].hidden = false
        // console.log(
        //   'showDetailHeadings - showing col = ' +
        //     this.headings[colNumbers[i]].field,
        // )
        i++
      }
    },
    //========================================================
    // show summary headings
    //-----------------------
    showSummaryHeadings() {
      // console.log('-------------------------')
      // console.log('IN showSummaryHeadings')
      let i = 0
      //------------------------
      // get toggle col numbers
      //------------------------
      let colNumbers = getDescSummaryCols({
        headings: this.headings,
        filter_status: this.filterStatus,
        view_round: this.view_round,
        cur_round: this.arena_round,
      })
      //---------------
      // hide all cols
      //---------------
      this.headings.map((a) => (a.hidden = true))
      //-------------------
      // show summary cols
      //-------------------
      this.toggleDesc = true
      //-------------------
      // show summary cols
      //-------------------
      while (i < colNumbers.length) {
        this.headings[colNumbers[i]].hidden = false
        // console.log(
        //   'showSummaryHeadings - showing col = ' +
        //     this.headings[colNumbers[i]].field,
        // )
        i++
      }
    },
    //========================================================
    // show number cols
    //------------------
    showNumberCols() {
      // console.log("-------------------");
      // console.log("IN showNumberCols");
      //---------------------------
      // store cur active col idxs
      //---------------------------
      this.curActiveColInds = storeHiddenCols(this.headings)
      //---------------
      // hide all cols
      //---------------
      this.headings.map((a) => (a.hidden = true))
      //-----------------
      // getToggleSRCols
      //-----------------
      let colNumbers = getToggleSRCols(this.headings)

      let i = 0
      //-----------
      // show cols
      //-----------
      while (i < colNumbers.length) {
        this.headings[colNumbers[i]].hidden = false
        i++
      }
    },
    //========================================================
    // show extra cols
    //-----------------
    showExtraCols() {
      console.log('-------------------')
      console.log('IN showExtraCols')

      // ------------------
      // store hidden cols
      this.curActiveColInds = storeHiddenCols(this.headings)

      // ------------------------------------------------
      // get last round action images for all combatants
      this.combatants.forEach((combatant) => {
        console.log(
          'getting image for combatant = ' + combatant.name,
          ' details = ',
          combatant,
        )

        combatant.last_action_image = getCombatantLastRoundActionImage(
          combatant,
          this.maxCA,
        )
        combatant.next_action_image = ''
        combatant.no_attack_reason = ''
      })

      // --------------
      // hide all cols
      this.headings.map((a) => (a.hidden = true))

      // ---------------------
      // set extra col images
      let colNumbers = getToggleExtraCols(this.headings)

      // -----------------------------------------------
      // load combatant extra images (pending or blank)
      console.log(
        'showExtraCols before loadCombatantExtraImages = ',
        this.combatants.map((combatant) => ({
          name: combatant.name,
          ca5_name: combatant.ca_name5,
          ca5_slug: combatant.ca_slug5,
          extra: combatant.extra,
          no_attack_reason: combatant.no_attack_reason,
        })),
      )
      loadCombatantExtraImages(this.combatants)

      console.log(
        'showExtraCols after loadCombatantExtraImages = ',
        this.combatants.map((combatant) => ({
          name: combatant.name,
          ca5_name: combatant.ca_name5,
          ca5_slug: combatant.ca_slug5,
          extra: combatant.extra,
          no_attack_reason: combatant.no_attack_reason,
        })),
      )

      // ----------------
      // show extra cols
      let i = 0
      while (i < colNumbers.length) {
        this.headings[colNumbers[i]].hidden = false
        i++
      }

      // -------------------
      // for all combatants
      Promise.all(
        this.combatants.map(async (combatant) => {
          // ---------------------------------------------------------------
          // get [any] next action for combatant (where continuous actions)
          const nextAction = await getCombatantNextRoundAction(
            combatant,
            this.arena_round,
          )

          // -------------------------------------------------------
          // set on counters flag for combatant (where on counters)
          let onCounters = false

          // -----------------------------------------
          // got next action - load next action image
          if (nextAction) {
            combatant.next_action_image = nextAction?.action_image || ''
          } else {
            // ------------------------------------------------------
            // get any next marker for combatant (where on counters)
            const nextMarker = await getCombatantNextRoundMarker(
              combatant,
              this.arena_round,
            )

            // ------------------------------------------
            //  got next marker - load next action image
            if (nextMarker) {
              combatant.next_action_image = nextMarker.marker_image || ''

              // ---------------------
              // set on counters flag
              if (nextMarker.marker_name == 'counter') {
                onCounters = true
              }
            }
          }

          console.log(
            'COMBATANT REASONS = ' + combatant.name + ' combatant = ',
            combatant,
          )

          // ----------------------------------------------
          // get extra attack invalid reason for combatant
          combatant.no_attack_reason = getCombatantExtraAttackInvalidReason({
            combatant,
            maxCA: this.maxCA,
            maxCR: this.maxCR,
            nextAction,
            onCounters,
          })

          console.log('showExtraCols after invalid reason calculation = ', {
            name: combatant.name,
            extra: combatant.extra,
            ca5_name: combatant.ca_name5,
            ca5_slug: combatant.ca_slug5,
            nextActionName: nextAction?.action_name || '',
            onCounters,
            no_attack_reason: combatant.no_attack_reason,
          })

          // ---------------------------------------------------------------------------------
          // defensive step 1 - ensures INVALID extra attacks remain invalid with blank image
          if (combatant.no_attack_reason !== '' && combatant.extra == true) {
            console.log(
              'OVERRIDING EXTRA combatant (should be INVALID) = ' +
                combatant.name +
                ' no_attack_reason = ' +
                combatant.no_attack_reason,
            )
            const idx = this.combatants.findIndex((c) => c.id === combatant.id)
            if (idx !== -1) {
              this.combatants[idx].extra = false
              loadSpecificCombatantExtraImage(idx, false, this.combatants)
              console.log(
                'showExtraCols after forcing invalid extra image = ',
                {
                  name: this.combatants[idx]?.name,
                  idx,
                  extra: this.combatants[idx]?.extra,
                  ca5_name: this.combatants[idx]?.ca_name5,
                  ca5_slug: this.combatants[idx]?.ca_slug5,
                  no_attack_reason: this.combatants[idx]?.no_attack_reason,
                },
              )
            }

            // -------------------------------------------------------------------------------
            // defensive step 2 - ensures VALID extra attacks remain valid with pending image
          } else if (
            combatant.no_attack_reason === '' &&
            combatant.extra == false
          ) {
            console.log(
              'OVERRIDING EXTRA combatant (should be VALID) = ' +
                combatant.name +
                ' no_attack_reason = ' +
                combatant.no_attack_reason,
            )
            const idx = this.combatants.findIndex((c) => c.id === combatant.id)
            if (idx !== -1) {
              this.combatants[idx].extra = true
              loadSpecificCombatantExtraImage(idx, false, this.combatants)
              console.log('showExtraCols after forcing valid extra image = ', {
                name: this.combatants[idx]?.name,
                idx,
                extra: this.combatants[idx]?.extra,
                ca5_name: this.combatants[idx]?.ca_name5,
                ca5_slug: this.combatants[idx]?.ca_slug5,
                no_attack_reason: this.combatants[idx]?.no_attack_reason,
              })
            }
          }

          console.log('----------------------------------------------------')

          console.log(
            'combatant = ' +
              combatant.name +
              ' no_attack_reason = ' +
              combatant.no_attack_reason,
          )
        }),
      )
    },
    //========================================================
    // load previous round action images
    //-----------------------------------
    async loadPreviousRoundActionImages(round = this.view_round) {
      console.log('-------------------------------------------')
      console.log('IN loadPreviousRoundActionImages round = ', round)
      if (round <= 1) {
        this.combatants.forEach((combatant) => {
          combatant.prev_round_action_image = ''
        })
        console.log(
          'loadPreviousRoundActionImages skipped because round <= 1; cleared images for combatants = ',
          this.combatants.map((combatant) => combatant.name),
        )
        return
      }

      await Promise.all(
        this.combatants.map(async (combatant) => {
          combatant.prev_round_action_image =
            await getCombatantPreviousRoundActionImage(combatant, round)

          console.log('loadPreviousRoundActionImages assigned = ', {
            name: combatant.name,
            slug: combatant.slug,
            prev_round_action_image: combatant.prev_round_action_image,
          })
        }),
      )

      console.log(
        'loadPreviousRoundActionImages complete summary = ',
        this.combatants.map((combatant) => ({
          name: combatant.name,
          has_prev_round_action_image: !!combatant.prev_round_action_image,
          prev_round_action_image: combatant.prev_round_action_image,
        })),
      )
    },
    //========================================================
    // filter combatant statuses
    //---------------------------
    filterCombatantStatuses(filterStatusDesc) {
      // console.log("---------------------------");
      // console.log("IN filterCombatantStatuses = " + filterStatusDesc);
      //-----------------------------------------------------
      // get all (in/actives) combatants (e.g. for bleeding)
      //-----------------------------------------------------
      if (filterStatusDesc == 'All') {
        this.combatants = this.getCombatants()
      } else {
        //-----------------------------
        // filter combatants by status
        //-----------------------------
        this.combatants = this.getCombatants().filter(
          (c) => c.status == filterStatusDesc,
        )
      }
    },
    //========================================================
    // show bleeder combatants only
    //------------------------------
    showBleederCombatants() {
      this.filterCombatantStatuses('All')
      this.combatants = getBleeders(this.combatants)
    },
    //========================================================
    // filter combatant dupe ranks
    //-----------------------------
    filterCombatantDupeRanks() {
      let combatants = this.getCombatants().filter((c) => c.dupe_rank == true)
      this.combatants = combatants
    },
    //========================================================
    // check force refilter
    //----------------------
    checkForceRefilter() {
      console.log('-----------------------')
      console.log('IN checkForceRefilter filterStatus = ' + this.filterStatus)
      //-----------------------------
      // get combatant status counts
      //-----------------------------
      let actives = this.getActiveCombatantCount()
      let inactives = this.getInactiveCombatantCount()
      // console.log("actives = " + actives + " inactives = " + inactives);
      //-------------------------------------------------------
      // no remaining rows for that status - flip filter status
      //--------------------------------------------------------
      if (
        (this.filterStatus && actives == 0) ||
        (!this.filterStatus && inactives == 0)
      ) {
        // console.log("no remaining rows - flip status...");
        //--------------------
        // flip filter status
        //--------------------
        this.filterStatus = !this.filterStatus
      }
      //-----------------------------
      // get cur filter status desc
      //-----------------------------
      let curFilterStatusDesc = getFilterStatusDesc(this.filterStatus)
      //----------------
      // perform filter
      //----------------
      this.filterCombatantStatuses(curFilterStatusDesc)
      //----------------
      // reset refilter
      //----------------
      this.forceRefilterCheck = false
    },
    //========================================================
    // check reactivated combatant requires active SR view
    //-----------------------------------------------------
    shouldShowActivesForReactivatedCombatant(combatant) {
      return (
        !this.filterStatus &&
        !this.toggleExtras &&
        combatant?.status == 'Active' &&
        (this.arena_in_progress || this.arena_rolled)
      )
    },
    //========================================================
    // show combatant panel
    //----------------------
    showCombatantPanel() {
      this.combatant = null
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // selected sr roll
    //------------------
    selectedSRRoll(idx, roll) {
      // console.log("--------------------");
      // console.log("IN selectedSRRoll");
      //------------------------------
      // update local array with roll
      //------------------------------
      this.combatants[idx].sr_roll = roll
      //----------------------------
      // update local sr roll image
      //----------------------------
      this.combatants[idx].sr_roll_image = getNumberImage(roll)
      //-----------
      // calc rank
      //-----------
      this.combatants[idx].rank = calcRank(this.combatants[idx])
      //--------------------------
      // check SR rolls completed
      //--------------------------
      if (!hasOutstandingSRRolls(this.combatants)) {
        // console.log("no o/s sr rolls...");
        //-----------------------------------------------
        // where multiple combatants - manage duplicates
        //-----------------------------------------------
        if (this.combatants.length > 1) {
          this.managePotentialDupeRolls()
          //-----------------
          // no o/s sr rolls
          //-----------------
        } else {
          //----------------------
          // manage post SR rolls
          //----------------------
          this.managePostSRRolls()
        }
      } else {
        // console.log("still o/s sr rolls...");
      }
    },
    //========================================================
    // manage potential dupe SR rolls
    //--------------------------------
    managePotentialDupeRolls() {
      console.log('--------------------')
      console.log('IN managePotentialDupeRolls')
      this.dupes = []
      //------------------------------
      // reset filter table (actives)
      //------------------------------
      let curFilterStatusDesc = getFilterStatusDesc(this.filterStatus)
      this.filterCombatantStatuses(curFilterStatusDesc)
      // console.log("reset filter to actives...");
      //--------------------------
      // clear current dupe ranks
      //--------------------------
      // console.log("clear dupe ranks...");
      clearLocalCombatantDupeRanks(this.combatants)
      //-----------------
      // check for dupes
      //-----------------
      // console.log("check for dupe ranks...");
      this.dupes = getDuplicateRanks(this.combatants)
      //-------------
      // where dupes
      //-------------
      if (this.dupes.length > 1) {
        //----------------------------
        // check auto troop roll-offs
        //----------------------------
        if (this.config.auto_troop_roll_offs) {
          let [still_dupes, troop_roll_offs] = autoTroopRollOffs(this.dupes)
          //----------------------------------------------
          // dupes remaining - load up for dupe treatment
          //----------------------------------------------
          if (still_dupes) {
            this.dupes = still_dupes
          } else {
            //-------------------------------------------
            // else no dupes remaining - clear out dupes
            //-------------------------------------------
            this.dupes = Array()
          }
          //------------------------------------------
          // update combatants troops auto rolled-off
          //------------------------------------------
          if (troop_roll_offs && troop_roll_offs.length > 1) {
            this.manageAutoRollOffs(troop_roll_offs)
            //----------------------------------------
            // trigger re-checking for more rank dupes
            //-----------------------------------------
            this.emitter.emit('roll-off-complete')
          }
        }
        //---------------------------------------------
        // where STILL dupes (in case all auto-rolled)
        //---------------------------------------------
        if (this.dupes.length > 1) {
          //----------------
          // load sr images
          //----------------
          this.dupes = loadCombatantsSRImages(this.dupes)
          //----------------------------------------
          // update local combatants with dupe tags
          //----------------------------------------
          this.dupes.forEach((d) => {
            //--------------------------
            // get combatant idx for id
            //--------------------------
            let idx = getCombatantIndexById(d.id)
            this.combatants[idx] = d
            // console.log(
            //   "dup list name = " + d.name + " is ranked = " + d.is_ranked
          })
          //--------------------------------
          // filter combatants by dupe rank
          //--------------------------------
          // console.log("gonna filter dupe ranks...");
          this.filterCombatantDupeRanks()
          //-----------------------------------
          // where auto-rolled + no non-o/s srs
          //------------------------------------
          if (!this.toggleNumbers) {
            this.toggleNumbers = true
          }
          //----------------------
          // manage post sr rolls
          //----------------------
        } else {
          this.managePostSRRolls()
        }
        //----------------------
        // manage post sr rolls
        //----------------------
      } else {
        this.managePostSRRolls()
      }
    },
    //========================================================
    // manage auto roll-offs
    //-----------------------
    manageAutoRollOffs(roll_offs) {
      console.log('--------------------')
      console.log('IN manageAutoRollOffs count = ' + roll_offs.length)
      roll_offs.forEach((r) => {
        let idx = this.combatants.findIndex((c) => c.id == r.id)
        this.combatants[idx].roll_off = r.roll_off
      })
    },
    //========================================================
    // selected roll-off
    //-------------------
    selectedRollOff(idx, roll) {
      console.log('--------------------')
      console.log('IN selectedRollOff')
      //------------------------------
      // update local array with roll
      //------------------------------
      this.combatants[idx].roll_off = roll
      //----------------------------
      // update local roll-off image
      //----------------------------
      this.combatants[idx].roll_off_image = getNumberImage(roll)
      //----------------------
      // disable panel number
      //----------------------
      this.emitter.emit('disable-panel-number', this.selected_number)
      //----------------------
      // clear selected number
      //----------------------
      this.selected_number = 0
      this.selected_number_image = null
      //--------------------------
      // check roll-offs completed
      //--------------------------
      if (!hasOutstandingRollOffs(this.dupes)) {
        // console.log("no o/s roll offs...");
        //-------------------------------------
        // set dupe rank/dex combos as processed
        //---------------------------------------
        this.dupes.forEach((d) => {
          d.dupe_processed = true
        })
        //-------------------------
        // re-enable panel numbers
        //-------------------------
        this.emitter.emit('reset-panel-numbers')
        //----------------------------------------
        // trigger re-checking for more rank dupes
        //-----------------------------------------
        this.emitter.emit('roll-off-complete')
      } else {
        // console.log("still got roll-offs to do...");
      }
    },
    //========================================================
    // manage post SR rolls
    //----------------------
    managePostSRRolls() {
      console.log('--------------------')
      console.log('IN managePostSRRolls')
      //------------------------
      // filter table (actives)
      //------------------------
      let curFilterStatusDesc = getFilterStatusDesc(this.filterStatus)
      this.filterCombatantStatuses(curFilterStatusDesc)
      //-------------------------
      // refresh combatant order
      //-------------------------
      this.combatants = refreshCombatantOrder(this.combatants)
      //---------------
      // reset columns
      //---------------
      this.toggleNumbers = false
      //-------------------------------------------
      // process new round where combat active but
      // not already started i.e. must be re-roll
      //-------------------------------------------
      if (this.arena_status == 'Active' && !this.arena_rolled) {
        this.newRoundHarness()
      }
      //-----------------------------------------
      // set srs as rolled if not already rolled
      //-----------------------------------------
      if (!this.arena_rolled) {
        this.arena_rolled = true
      }
      //--------------------------------------------
      // update active combatants with rank details
      //--------------------------------------------
      this.updateCombatantRankDetails()
      //----------------------
      // update rolled status
      //----------------------
      if (!this.arena_in_progress) {
        this.updateRolledStatus()
      }
      //-----------------------------------
      // set local combatants as is-ranked
      //-----------------------------------
      this.combatants = setLocalCombatantsIsRanked(this.combatants)
    },
    //
    //************************************
    //
    //        POST-API CALL TASKS
    //
    //************************************
    //========================================================
    // load specific combatants (*** when loading new/updated combatant(s) ***)
    //--------------------------
    loadSpecificCombatants(load_combatants) {
      console.log('--------------------------')
      console.log('IN loadSpecificCombatants = ' + load_combatants)

      let load_responses = load_combatants
      load_combatants = loadLocalCombatantProps(
        load_combatants.map((response) => response.combatant || response),
      )

      load_combatants.forEach((c, idx) => {
        //-----------
        // set image
        //-----------
        let response_image = load_responses[idx].combatant
          ? load_responses[idx].image
          : null
        let img = response_image || (c.image ? { image: c.image } : null)
        //-----------------------
        // get combatant indices
        //-----------------------
        let filter_idx = getCombatantFilterIndexById(this.combatants, c.id)
        // console.log("filter_idx = " + filter_idx);
        let full_idx = getCombatantIndexById(c.id)
        // console.log("full_idx = " + full_idx);
        let existing_combatant =
          filter_idx < 0 ? null : this.combatants[filter_idx]
        //-------------
        // images parm
        //-------------
        let parm = {
          idx: full_idx,
          img: img,
        }
        //-----------------------
        // load combatant images
        //-----------------------
        loadSpecificCombatantImages(parm)
        //---------------------------------
        // load combatant headline markers
        //---------------------------------
        loadSpecificCombatantHeadlineMarkers(full_idx)
        //--------------------------------
        // get actions for specific round
        //--------------------------------
        let round_actions = c.actions.filter((a) => a.round == this.view_round)
        //--------------------------------
        // get markers for specific round
        //--------------------------------
        let round_markers = c.markers.filter((a) => a.round == this.view_round)
        //----------------------------------
        // load combatant actions & markers
        //----------------------------------
        let del_arrays = loadSpecificCombatantActions(
          round_actions,
          round_markers,
          full_idx,
        )
        //---------------------------------
        // delete actions where ca reduced
        //---------------------------------
        if (del_arrays.actions && del_arrays.actions.length > 0) {
          for (let i = 0; i < del_arrays.actions.length; i++) {
            //----------------------
            // delete action for ca
            //----------------------
            this.deleteSlotHarness(del_arrays.actions[i])
          }
        }
        //---------------------------------
        // delete markers where ca reduced
        //---------------------------------
        if (del_arrays.markers && del_arrays.markers.length > 0) {
          for (let i = 0; i < del_arrays.markers.length; i++) {
            //---------------------
            // delete marker for ca
            //----------------------
            this.deleteSlotHarness(del_arrays.markers[i])
          }
        }
        //-------------------------
        // load combatant reactions
        //--------------------------
        loadSpecificCombatantReactions(this.view_round, c.reactions, full_idx)
        //---------------------------
        // get new/updated combatant
        //---------------------------
        let this_combatant = getCombatantById(c.id)

        if (existing_combatant && this_combatant) {
          ;[
            'prev_round_action_image',
            'last_action_image',
            'next_action_image',
            'no_attack_reason',
            'extra',
            'dupe_processed',
            'dupe_rank',
            'roll_off',
            'is_ranked',
            'headline_marker1',
            'headline_marker2',
            'headline_marker3',
            'headline_marker4',
          ].forEach((field) => {
            if (Object.hasOwn(existing_combatant, field)) {
              this_combatant[field] = existing_combatant[field]
            }
          })
        }
        //----------------------------------------
        // add combatant to local array where new
        //----------------------------------------
        if (filter_idx < 0) {
          this.combatants.push(this_combatant)
          //------------------------------------
          // refresh existing combatant locally
          //------------------------------------
        } else {
          this.combatants[filter_idx] = this_combatant
        }
      })
    },
    //
    //************************************
    //
    //            CRUD CALLS
    //
    //************************************
    //
    //========================================================
    // add combatant harness
    //-----------------------
    async addMultipleCombatantsHarness(args) {
      console.log('----------------------------------')
      console.log('IN addMultipleCombatantsHarness...')
      this.loadReady = false
      let call = useAddMultipleCombatants(args)
      await call
        .then((responses) => {
          //-----------------
          // load combatants
          //-----------------
          this.loadSpecificCombatants(responses)
          //-------------------------
          // refresh combatant order
          //-------------------------
          this.combatants = refreshCombatantOrder(this.combatants)
          //---------------------------------
          // isolate ids for scenario checks
          //---------------------------------
          let scenario_ids = responses.map((c) => c.combatant.id)
          //------------------
          // manage scenarios
          //------------------
          this.manageScenarios(scenario_ids)
          //------------------
          // show add message
          //------------------
          let msg = ''
          if (args.qty == 1) {
            msg = args.qty + ' combatant added'
          } else {
            msg = args.qty + ' combatants added'
          }
          showNotification(msg)
          //------------------------------------
          // force refilter where inactive view
          //------------------------------------
          this.filterStatus = true
          this.loadReady = true
        })
        .catch((err) => {
          handleError(err, 24)
        })
    },
    //========================================================
    // update multiple combatants harness
    //------------------------------------
    async updateMultipleCombatantsHarness(combatant_updates, msg) {
      console.log('----------------------------------')
      console.log('IN updateMultipleCombatantsHarness...')
      let call = useUpdateMultipleCombatants(combatant_updates)
      await call
        .then((combatants) => {
          let msg2
          //-----------------
          // load combatants
          //-----------------
          this.loadSpecificCombatants(combatants)
          //--------------
          // show message
          //--------------
          if (combatant_updates.length > 1) {
            msg2 = ' combatants '
          } else {
            msg2 = ' combatant '
          }
          showNotification(combatant_updates.length + msg2 + msg)
        })
        .catch((err) => {
          handleError(err, 41)
        })
    },
    //========================================================
    // update combatant harness
    //--------------------------
    async updateCombatantHarness(updatedCombatant) {
      console.log('--------------------')
      console.log('IN updateCombatantHarness')
      //------------------
      // update combatant
      //------------------
      let call = useEditCombatant(updatedCombatant)
      await call
        .then((response) => {
          //----------------
          // load combatant
          //----------------
          this.loadSpecificCombatants([response])
          //--------------------------------------------
          // preserve bleeders view after combatant edit
          //--------------------------------------------
          if (this.arena_bleeders_shown) {
            this.showBleederCombatants()
          }
          //-----------------------------------------
          // check force re-filter on status changes
          //-----------------------------------------
          if (this.forceRefilterCheck && !this.arena_bleeders_shown) {
            this.checkForceRefilter()
          }
          //----------------
          // load sr images
          //----------------
          this.combatants = loadCombatantsSRImages(this.combatants)
          //-------------------------
          // refresh combatant order
          //-------------------------
          this.combatants = refreshCombatantOrder(this.combatants)
          //------------------------------------------------
          // get srs where active + srs already rolled AND:
          // a) combatant sr = 0, OR
          // b) got dupe ranks cos changed sr dets in panel
          //------------------------------------------------
          if (
            response.combatant.status == 'Active' &&
            this.arena_rolled &&
            (response.combatant.sr_roll == 0 ||
              hasDuplicateRanks(this.combatants))
          ) {
            this.manageRolls()
          }
          //-----------------------------------------------------
          // get current action harness where combat in progress
          //-----------------------------------------------------
          if (this.arena_in_progress) {
            // console.log("calling getCurrentActionHarness 1...");
            this.getCurrentActionHarness()
          }
          //--------------
          // show message
          //--------------
          showNotification(
            formatCombatantUpdateMessage({
              payload: updatedCombatant,
              originalPayload: this.originalCombatantPayload,
            }),
          )
        })
        .catch((err) => {
          handleError(err, 25)
        })
    },
    //========================================================
    // update combatant field harness
    //--------------------------------
    async updateCombatantFieldsHarness(
      updatedCombatant,
      combatant_id = null,
      notificationMessage = '',
    ) {
      console.log('--------------------------------------')
      console.log('IN updateCombatantFieldsHarness')
      // window.displayPairs(updatedCombatant);
      //------------------
      // update combatant
      //------------------
      let call = useUpdateCombatant(updatedCombatant)
      await call
        .then((response) => {
          let shouldRestoreExtraState =
            this.toggleExtras && this.view_round == this.arena_round
          const responseCombatant = response.combatant || response

          if (
            shouldRestoreExtraState &&
            this.arena_extra_action > 0 &&
            this.arena_extra_action_save == 0
          ) {
            this.arena_extra_action_save = this.arena_extra_action
            this.arena_extra_action = 0
          }

          console.log(
            'back from updateCombatantFieldsHarness call - response = ',
            response,
          )
          //----------------
          // load combatant
          //----------------
          this.loadSpecificCombatants([response])
          //--------------------------------------------
          // preserve bleeders view after combatant edit
          //--------------------------------------------
          if (this.arena_bleeders_shown) {
            this.showBleederCombatants()
          }
          //----------------------------------------------------------
          // reactivated combatants must switch to the active table so
          // missing-action, SR roll, and roll-off handling can run
          //----------------------------------------------------------
          if (this.arena_bleeders_shown) {
            this.forceRefilterCheck = false
          } else if (
            this.shouldShowActivesForReactivatedCombatant(responseCombatant)
          ) {
            this.filterStatus = true
            this.filterCombatantStatuses('Active')
            this.forceRefilterCheck = false
          } else if (this.forceRefilterCheck) {
            //-----------------------------------------------------------
            // force re-filter of cur filter status where sub-status chg
            //-----------------------------------------------------------
            this.checkForceRefilter()
          }
          //-------------------------
          // refresh combatant order
          //-------------------------
          this.combatants = refreshCombatantOrder(this.combatants)
          //----------------
          // load sr images
          //----------------
          this.combatants = loadCombatantsSRImages(this.combatants)

          if (
            this.getActiveCombatantCount() == 0 &&
            (this.toggleExtras ||
              this.arena_extra_ready ||
              this.arena_extra_action > 0)
          ) {
            this.toggleExtras = false
            this.arena_extra_ready = false
            this.arena_extra_action = 0
            this.arena_extra_action_save = 0
            this.unused_action_count = 0
            this.setUnusedActionCount(0)
            this.combatants = setLocalCombatantsExtras(this.combatants, [])
            shouldRestoreExtraState = false

            if (this.toggleDesc) {
              this.showSummaryHeadings()
            } else {
              this.showDetailHeadings()
            }
          }

          if (shouldRestoreExtraState && this.arena_extra_action_save > 0) {
            this.arena_extra_action = this.arena_extra_action_save
            this.arena_extra_action_save = 0
            this.showExtraCols()
          }

          if (
            this.toggleExtras &&
            this.arena_in_progress &&
            this.view_round == this.arena_round &&
            this.arena_extra_action == 0
          ) {
            this.showExtraCols()
          }
          //------------------
          // manage scenarios
          //------------------
          if (combatant_id) {
            this.manageScenarios([combatant_id])
          } else {
            this.loadReady = true
          }
          //-------------------
          // update scoreboard
          //-------------------
          this.emitter.emit('update-scoreboard')
          //--------------
          // show message
          //--------------
          const message =
            notificationMessage ||
            formatCombatantUpdateMessage({
              payload: updatedCombatant,
            })
          showNotification(message)
        })
        .catch((err) => {
          handleError(err, 26)
        })
    },
    //========================================================
    // update combatant surprise harness
    //-----------------------------------
    async updateCombatantSurpriseHarness(updatedCombatant) {
      console.log('--------------------------------------')
      console.log('IN updateCombatantSurpriseHarness')
      //------------------
      // update combatant
      //------------------
      let call = useUpdateCombatant(updatedCombatant)
      await call
        .then(() => {
          //-------------------------
          // refresh combatant order
          //-------------------------
          this.combatants = refreshCombatantOrder(this.combatants)
          //--------------
          // show message
          //--------------
          const message = formatCombatantUpdateMessage({
            payload: updatedCombatant,
            type: 'surprise',
          })
          showNotification(message)
        })
        .catch((err) => {
          handleError(err, 39)
        })
    },
    //========================================================
    // delete combatant harness
    //--------------------------
    async deleteCombatantHarness(id) {
      //---------------
      // get combatant
      //---------------
      const combatant = getCombatantById(id)
      //-----------------------
      // call delete combatant
      //-----------------------
      let call = useDeleteCombatant(combatant.slug)
      await call
        .then(() => {
          //--------------------------------------------------------
          // check force re-filter when no combatants of cur status
          //--------------------------------------------------------
          this.checkForceRefilter()
          //-----------------------------------------------------
          // get current action harness where combat in progress
          //-----------------------------------------------------
          if (this.arena_in_progress) {
            // console.log("calling getCurrentActionHarness 2...");
            this.getCurrentActionHarness()
          }
          //--------------
          // show message
          //-------------
          showNotification('Combatant ' + combatant.name + ' deleted')
        })
        .catch((err) => {
          handleError(err, 27)
        })
    },
    //========================================================
    // delete slot harness
    //---------------------
    async deleteSlotHarness(parm) {
      console.log('-----------------------')
      console.log('IN deleteSlotHarness')
      const logContext = this.getCombatLogContext()
      //------------------
      // call delete slot
      //------------------
      let call = useDeleteSlot(parm)
      await call
        .then(() => {
          //-----------------------------------------------------
          // get current action harness where combat in progress
          // (flag deleted slot as cur action may be reduced)
          //-----------------------------------------------------
          if (this.arena_in_progress) {
            // console.log("calling getCurrentActionHarness 3...");
            this.getCurrentActionHarness(true)
          }
          if (parm.logCombatEvent === true) {
            let category = ''
            let name = ''

            if (parm.slot == 'Action') {
              category = 'action'
              name = parm.removed_name
            } else if (parm.slot == 'Reaction') {
              category = 'reaction'
              name = parm.removed_name
            } else if (parm.slot == 'Marker') {
              category = 'marker'
              name = parm.marker_name
            }

            if (category && name) {
              this.writeCombatEventLog({
                combatantId: parm.id,
                category,
                name,
                undo: true,
                round: logContext.round,
                actionNumber: logContext.actionNumber,
                extraAttacks: logContext.extraAttacks,
              }).catch((err) => {
                handleError(err, 43)
              })
            }
          }
          //-------------
          // show messge
          //-------------
          if (parm.notify) {
            showNotification(parm.slot + ' deleted')
          }
        })
        .catch((err) => {
          handleError(err, 28)
        })
    },
    //========================================================
    // delete blank slot markers
    //---------------------------
    deleteBlankSlotMarkers(delete_markers_array) {
      console.log('-----------------------')
      console.log('IN deleteBlankSlotMarkers')
      //---------------------
      // loop deletion array
      //---------------------
      for (let i = 0; i < delete_markers_array.length; i++) {
        //------------------------------
        // perform marker call (delete)
        //------------------------------
        this.deleteSlotHarness(delete_markers_array[i])
        //----------------------
        // reduce markers count
        //----------------------
        reduceMarkersCount(delete_markers_array[i].id)
      }
    },
    //========================================================
    // edit combat action harness
    //----------------------------
    async editCombatActionHarness(
      action_parm,
      cur_action_parm,
      selected_action,
    ) {
      console.log('------------------------------')
      console.log('IN editCombatActionHarness')

      console.log('action_parm = ', action_parm)
      console.log('cur_action_parm = ', cur_action_parm)
      console.log('selected_action = ', selected_action)

      const logContext = this.getCombatLogContext({
        actionNumber: action_parm?.number,
      })

      // covers where missing actions requires no-action overriding cur selected action
      if (!selected_action) {
        selected_action = this.selected_action
      }
      //--------------------
      // edit combat action
      //--------------------
      let call = useEditCombatAction(action_parm)
      await call
        //------------
        // successful
        //------------
        .then((combat_action) => {
          let slot_parm = {
            combatant: action_parm.combatant,
            ca: combat_action,
            type: 'action',
          }
          //------------------------------------
          // update ca count/slug for combatant
          //------------------------------------
          updateCASlot(slot_parm)
          //-------------------------------------
          if (selected_action.actual == false) {
            shiftPendingCAsRight(cur_action_parm, action_parm.number)
            //----------------------------------------------
            // new IS actual action + replaces existing...
            //----------------------------------------------
          } else if (cur_action_parm.slot_slug != '') {
            //--------------------------
            // shift pending slots left
            //--------------------------
            let delete_markers_array = shiftPendingCAsLeft(
              cur_action_parm,
              action_parm.number,
              selected_action,
            )
            //-------------------------------------
            // delete markers if slot is now blank
            //-------------------------------------
            if (delete_markers_array.length > 0) {
              this.deleteBlankSlotMarkers(delete_markers_array)
            }
          }
          //-----------------------------------------------------
          // get current action harness where combat in progress
          // (not in extra mode)
          //-----------------------------------------------------
          if (this.arena_in_progress && this.arena_extra_action == 0) {
            // console.log("calling getCurrentActionHarness 4...");
            this.getCurrentActionHarness()
          }
          //-----------------------
          // check extras complete
          //-----------------------
          if (this.arena_extra_action > 0) {
            this.isExtrasComplete()
            this.unused_action_count = getUnusedExtraActions(this.combatants)
            //-------------------------
            // set unused action count
            //-------------------------
            this.setUnusedActionCount(this.unused_action_count)
            //-------------------
            // update scoreboard
            //-------------------
            this.emitter.emit('update-scoreboard')
          }
          this.writeCombatEventLog({
            combatantId: action_parm.combatant,
            category: 'action',
            name: selected_action?.name,
            round: logContext.round,
            actionNumber: logContext.actionNumber,
            extraAttacks: logContext.extraAttacks,
          }).catch((err) => {
            handleError(err, 44)
          })
        })
        .catch((err) => {
          handleError(err, 29)
        })
    },
    //========================================================
    // is extras complete
    //--------------------
    isExtrasComplete() {
      // console.log("------------------");
      // console.log("IN isExtrasComplete");
      let extra_slug = 'ca_slug' + this.ca_extra
      //----------------
      // get o/s extras
      //----------------
      let extras = this.combatants.some(
        (a) => a.extra == true && a[extra_slug] == '',
      )
      //------------------
      // extras still o/s
      //------------------
      if (extras) {
        this.arena_round_completed = false
      } else {
        const completionMessage =
          'Extra attacks for Round ' + this.arena_round + ' completed'

        showNotification(completionMessage, { log: false })
        this.writeArenaEventLog(completionMessage).catch((err) => {
          handleError(err, 55)
        })

        //---------------------------------
        // check for bleeders (in/actives)
        //---------------------------------
        this.arena_bleeders_ready = hasBleeders(this.getCombatants())
        if (this.arena_bleeders_ready) {
          showNotification('Bleeders present')
        }
        //-------------
        // round ended
        //-------------
        this.endRoundHarness()
        if (!this.arena_bleeders_ready && this.shouldAutoStartNewRound()) {
          this.checkRetainSRRolls()
        }
      }
    },
    //========================================================
    // edit combat marker harness
    //----------------------------
    async editCombatMarkerHarness(marker_parm, marker_name) {
      const logContext = this.getCombatLogContext()
      //--------------------
      // edit combat marker
      //--------------------
      let call = useEditCombatMarker(marker_parm)
      await call
        //------------
        // successful
        //------------
        .then((combat_marker) => {
          let slot_parm = {
            combatant: marker_parm.combatant,
            ca: combat_marker,
            type: 'marker',
          }
          //------------------------------------
          // update ca count/slug for combatant
          //------------------------------------
          updateMarkerSlot(slot_parm)
          //-----------------------------------------------------
          // get current action harness where combat in progress
          // (not in extra mode)
          //-----------------------------------------------------
          if (this.arena_in_progress && this.arena_extra_action == 0) {
            // console.log("calling getCurrentActionHarness 5...");
            this.getCurrentActionHarness()
          }
          this.writeCombatEventLog({
            combatantId: marker_parm.combatant,
            category: 'marker',
            name: marker_name,
            round: logContext.round,
            actionNumber: logContext.actionNumber,
            extraAttacks: logContext.extraAttacks,
          }).catch((err) => {
            handleError(err, 45)
          })
        })
        .catch((err) => {
          handleError(err, 30)
        })
    },
    //========================================================
    // edit combat reaction harness
    //------------------------------
    async editCombatReactionHarness(reaction_parm, reaction_name) {
      const logContext = this.getCombatLogContext()
      //--------------------
      // edit combat reaction
      //--------------------
      let call = useEditCombatReaction(reaction_parm)
      await call
        //------------
        // successful
        //------------
        .then((combat_reaction) => {
          let slot_parm = {
            combatant: reaction_parm.combatant,
            // reaction: reaction_parm.action,
            cr: combat_reaction,
          }
          //------------------------------------
          // update cr count/slug for combatant
          //------------------------------------
          updateCRSlot(slot_parm)
          //----------------------------------------------------------------
          // where currently extra mode - check still so after new reaction
          //----------------------------------------------------------------
          if (this.arena_extra_ready) {
            this.getExtraAttacksHarness()
          }
          this.writeCombatEventLog({
            combatantId: reaction_parm.combatant,
            category: 'reaction',
            name: reaction_name,
            round: logContext.round,
            actionNumber: logContext.actionNumber,
            extraAttacks: logContext.extraAttacks,
          }).catch((err) => {
            handleError(err, 46)
          })
        })
        .catch((err) => {
          handleError(err, 31)
        })
    },
    //========================================================
    // load campaign deployable characters harness
    //---------------------------------------------
    async loadCampaignDeployableCharactersHarness(message = '') {
      console.log('---------------------------')
      console.log('IN loadCampaignDeployableCharactersHarness')

      const this_store = store
      const copy_image = true

      this.loading_msg = 'loading characters...'

      //------------------------------------
      // 1. Load deployable characters
      //------------------------------------
      const deployable_characters = await this_store.dispatch(
        'characters/loadCampaignDeployableCharacters',
        this.arena_slug,
      )

      //------------------------------------
      // 2. Deploy characters (sequential)
      //------------------------------------
      let j = 1
      for (const character of deployable_characters) {
        this.loading_msg = `deploying ${j} of ${deployable_characters.length} characters...`

        await resolveCharacterForCombatDeploy(character)
        j++
      }
      this.loading_msg = 'creating combatants...'

      //-------------------------------------------------------------------
      // 3. create combatants (parallel) BUT progress updates inside async
      //-------------------------------------------------------------------
      const creationPromises = deployable_characters.map(async (character) => {
        const new_combatant = convertCharacterToCombatant(character)
        if (!new_combatant) return null

        console.log(
          'deploying character ' + new_combatant.name + ' as combatant ',
          new_combatant,
        )

        const response = await useEditCombatant(new_combatant, copy_image)
        return response
      })

      //------------------------------------
      // 4. Wait for all combatants to finish
      //------------------------------------
      const responses = await Promise.all(creationPromises)
      //----------------------------------------
      // 5. Load deployed characters as combatants
      //----------------------------------------
      this.loadSpecificCombatants(responses)
      //-------------------------
      // 6. Refresh combatant order
      //-------------------------
      this.combatants = refreshCombatantOrder(this.combatants)
      this.isInitialisingCombat = false
      this.loadReady = true
      //------------------------------------
      // 7. Force refilter where inactive view
      //------------------------------------
      this.filterStatus = true

      if (message) {
        showNotification(message)
      }
    },
    //========================================================
    // update combatant rank details
    //-------------------------------
    async updateCombatantRankDetails() {
      console.log('--------------------')
      console.log('IN updateCombatantRankDetails')
      //-----------------
      // define promises
      //-----------------
      let promises = []
      //------------------------
      // loop active characters
      //------------------------
      for (let i = 0; i < this.combatants.length; i++) {
        let c = this.combatants[i]
        //-----------------
        // set update parm
        //-----------------
        let update_parm = {
          name: c.name,
          slug: c.slug,
          seq: c.seq,
          sr_roll: c.sr_roll,
          roll_off: c.roll_off,
          rank: c.rank,
        }
        //------------------
        // update combatant
        //------------------
        let p = useUpdateCombatant(update_parm).then((response) => {
          //------------------
          // reload combatant
          //------------------
          this.loadSpecificCombatants([response])
          return response
        })
        //---------------
        // store promise
        //---------------
        promises.push(p)
      }
      //--------------
      // all finished
      //--------------
      Promise.all(promises).then(() => {
        //--------------------------
        // update local rolled flag
        //--------------------------
        // if (!this.arena_rolled) {
        //   this.arena_rolled = true;
        //---------------------------
        // store arena rolled status
        //---------------------------
        //   this.updateArenaRolledStatus(this.arena_rolled);
        // }
      })
    },
    //========================================================
    // manageRolls
    //-------------
    manageRolls() {
      console.log('---------------------')
      console.log('IN manageRolls')
      const config = this.refreshCombatConfig()
      //------------------------------------------
      // clear ranks where srs not already rolled
      //------------------------------------------------------
      // allows for (a) additional troops to be
      // added or (b) chars deployed w/o impacting prev rolls
      //------------------------------------------------------
      if (!this.arena_rolled) {
        this.combatants = clearLocalCombatantRanks(this.combatants)
      }
      //---------------------------------------------
      // auto-populate eligible strike rank rolls
      //---------------------------------------------
      this.combatants = autoRollCombatantSRs(this.combatants, config)
      //----------------------------
      // refresh order/recalc ranks
      //----------------------------
      this.combatants = refreshCombatantOrder(this.combatants)
      //----------------
      // load sr images
      //----------------
      this.combatants = loadCombatantsSRImages(this.combatants)
      //------------------------
      // check for o/s sr rolls
      //------------------------
      if (hasOutstandingSRRolls(this.combatants)) {
        //========================================================
        // A. have o/s sr rolls after auto-roll
        //========================================================
        console.log('have o/s rolls...')
        //------------------
        // show number cols
        //------------------
        this.toggleNumbers = true
        //--------------
        // show message
        //--------------
        let msg = 'Roll strike ranks'
        showNotification(msg)
      } else {
        //========================================================
        // B. no o/s sr rolls after auto-roll
        //========================================================
        // console.log("no o/s rolls...");
        //----------------------------------------------
        // multiple combatants - manage potential dupes
        //----------------------------------------------
        if (this.combatants.length > 1) {
          this.managePotentialDupeRolls()
          //------------------
          // single combatant
          //------------------
        } else {
          console.log('manageRolls4...')
          //----------------------
          // manage post SR rolls
          //----------------------
          this.managePostSRRolls()
        }
      }
    },
    //
    //************************************
    //
    //          PRE-CALL TASKS
    //
    //************************************
    //
    //========================================================
    // prepare action call
    //---------------------
    prepActionCall(cur_action_parm, selected_action) {
      console.log('--------------------------')
      console.log('IN prepActionCall')
      let ca_num
      // console.log("cur_action_parm.slot_num = " + cur_action_parm.slot_num)
      // console.log("cur_action_parm.slot_slug = " + cur_action_parm.slot_slug)
      //-------------------------------
      // get combatant for id received
      //-------------------------------
      let idx = getCombatantIndexById(cur_action_parm.id)
      //-------------------------------------------------------------------------
      // where replacing/specifying slot action (e.g. counter -> remove counter)
      //-------------------------------------------------------------------------
      if (cur_action_parm.slot_num > 0) {
        ca_num = cur_action_parm.slot_num
        // console.log("here1")
        //---------------------------------------------------
        // where no slug - new slot - get first pending slot
        //---------------------------------------------------
      } else if (cur_action_parm.slot_slug == '') {
        // console.log("here2")
        ca_num = getFirstPendingSlot(this.combatants[idx], selected_action)
        // console.log("ca_num = " + ca_num)
        //-----------------------------------------------------
        // delete existing marker IF in same pos as new action
        //-----------------------------------------------------
        this.prepDeleteMarker(ca_num, idx)
        //-----------------------------------------
        // where slug - use ca slot num = upd slot
        //-----------------------------------------
      } else {
        ca_num = cur_action_parm.slot.substr(2, 1)
        // console.log("here2")
        // console.log("ca_num = " + ca_num)
      }
      // console.log("XX ca_num = " + ca_num)
      //--------------------------------
      // update combatant ca image/name
      //--------------------------------
      cur_action_parm.slot = 'ca' + String(ca_num)
      //
      let slot = 'ca' + ca_num
      let slot_name = 'ca_name' + ca_num
      this.combatants[idx][slot] = selected_action.image
      this.combatants[idx][slot_name] = selected_action.name
      //-----------------
      // set action parm
      //-----------------
      let action_parm = {
        round: this.view_round,
        number: ca_num,
        combatant: this.combatants[idx].id,
        action: selected_action.id,
        slug: cur_action_parm.slot_slug,
      }
      //-----------------
      // store ca change
      //-----------------
      this.editCombatActionHarness(
        action_parm,
        cur_action_parm,
        selected_action,
      )
    },
    //========================================================
    // prep delete marker
    //--------------------
    prepDeleteMarker(ca_num, idx) {
      console.log('------------------------')
      console.log('IN prepDeleteMarker')
      //----------
      // set vars
      //----------
      let marker_type = 'ca_type' + ca_num
      let marker_name = 'ca_name' + ca_num
      let marker_slug = 'ca_slug' + ca_num
      //---------------------------------------------------------
      // if first pending slot = existing marker - delete marker
      //---------------------------------------------------------
      if (this.combatants[idx][marker_type] == 'marker') {
        let name = this.combatants[idx][marker_name]
        //------------
        // get marker
        //------------
        let delete_marker = getMarker(name)
        //-----------------
        // set delete parm
        //-----------------
        let delete_parm = {
          id: this.combatants[idx].id,
          slug: this.combatants[idx][marker_slug],
          slot: 'Marker',
          num: ca_num,
          actual: delete_marker.actual,
          marker_name: delete_marker.name,
          notify: false,
        }
        this.emitter.emit('delete-marker', delete_parm)
      }
    },
    //========================================================
    // prepare marker call
    //---------------------
    prepMarkerCall(parm) {
      const selectedMarkerName = this.selected_marker?.name
      let ca_num
      //-------------------------------
      // get combatant for id received
      //-------------------------------
      let idx = getCombatantIndexById(parm.id)
      //-----------------------------------------------------
      // where specifying marker position (i.e non-counters)
      //-----------------------------------------------------
      if (parm.slot_num > 0) {
        ca_num = parm.slot_num
        //---------------------------------------------------
        // where no slug - new slot - get first pending slot
        //---------------------------------------------------
      } else if (parm.slot_slug == '') {
        ca_num = getFirstPendingSlot(this.combatants[idx], this.selected_marker)
        //--------------------------------------------------
        // delete existing marker in same pos as new action
        //--------------------------------------------------
        this.prepDeleteMarker(ca_num, idx)
        //-----------------------------------------
        // where slug - use ca slot num = upd slot
        //-----------------------------------------
      } else {
        ca_num = parm.slot.substr(2, 1)
      }
      //--------------------------------
      // update combatant ca image/name
      //--------------------------------
      let slot = 'ca' + ca_num
      let slot_name = 'ca_name' + ca_num
      this.combatants[idx][slot] = this.selected_marker.image
      this.combatants[idx][slot_name] = this.selected_marker.name
      //--------------------
      // increment counters
      //--------------------
      if (this.selected_marker.name == 'counter') {
        this.combatants[idx].counters++
      }
      //-----------------
      // set action parm
      //-----------------
      let new_parm = {
        round: this.view_round,
        number: ca_num,
        combatant: this.combatants[idx].id,
        marker: this.selected_marker.id,
        slug: parm.slot_slug,
      }
      //---------------------
      // store marker change
      //---------------------
      this.editCombatMarkerHarness(new_parm, selectedMarkerName)
    },
    //========================================================
    // prepare reaction call
    //-----------------------
    prepReactionCall(parm) {
      const selectedReactionName = this.selected_reaction?.name
      let cr_num
      //-------------------------------
      // get combatant for id received
      //-------------------------------
      let idx = getCombatantIndexById(parm.id)
      //-------------------
      // determine cr slot
      //-------------------
      cr_num = this.combatants[idx].reaction_count + 1
      let slot = 'cr' + cr_num
      let slot_name = 'cr_name' + cr_num
      //--------------------------------
      // update combatant cr image/name
      //--------------------------------
      this.combatants[idx][slot] = this.selected_reaction.image
      this.combatants[idx][slot_name] = this.selected_reaction.name
      //-------------------
      // set reaction parm
      //-------------------
      let new_parm = {
        round: this.view_round,
        number: cr_num,
        combatant: this.combatants[idx].id,
        reaction: this.selected_reaction.id,
        slug: parm.slot_slug,
      }
      //-----------------
      // store cr change
      //-----------------
      this.editCombatReactionHarness(new_parm, selectedReactionName)
    },
    captureLoggableActionNumber(actionNumber = null) {
      const resolvedActionNumber = Number(actionNumber)

      if (
        Number.isFinite(resolvedActionNumber) &&
        resolvedActionNumber > 0 &&
        resolvedActionNumber !== 99
      ) {
        this.lastLoggableActionNumber = resolvedActionNumber
      }

      return this.lastLoggableActionNumber
    },
    getCombatLogContext({
      round = null,
      actionNumber = null,
      extraAttacks,
    } = {}) {
      const resolvedRound = Number(round)
      const resolvedActionNumber = Number(actionNumber)
      const fallbackActionNumber =
        this.arena_action > 0 && this.arena_action !== 99
          ? this.arena_action
          : this.lastLoggableActionNumber

      const nextActionNumber =
        Number.isFinite(resolvedActionNumber) && resolvedActionNumber > 0
          ? resolvedActionNumber
          : fallbackActionNumber

      const nextExtraAttacks =
        typeof extraAttacks === 'boolean'
          ? extraAttacks
          : this.arena_extra_action > 0 ||
            this.arena_extra_ready ||
            (this.arena_round_completed &&
              this.combatants.some((entry) => entry?.extra === true))

      this.captureLoggableActionNumber(nextActionNumber)

      return {
        round:
          Number.isFinite(resolvedRound) && resolvedRound > 0
            ? resolvedRound
            : (this.view_round ?? this.arena_round ?? 0),
        actionNumber: nextActionNumber,
        extraAttacks: nextExtraAttacks,
      }
    },
    async writeCombatEventLog({
      combatantId,
      category,
      name,
      undo = false,
      round,
      actionNumber,
      extraAttacks,
    } = {}) {
      const arenaId = this.arena?.id || this.getCampaign()?.id
      if (!arenaId || !combatantId || !category || !name) {
        return
      }

      const combatant =
        getCombatantById(combatantId) ||
        this.combatants.find((entry) => entry?.id == combatantId)

      const logContext = this.getCombatLogContext({
        round,
        actionNumber,
        extraAttacks,
      })

      const description = buildCombatLogDescription({
        round: logContext.round,
        actionNumber: logContext.actionNumber,
        combatantName: combatant?.name,
        category,
        name,
        undo,
        extraAttacks: logContext.extraAttacks,
      })

      console.log('combat event log description = ', description)

      if (!description) {
        return
      }

      await this.queueCampaignLogWrite(() =>
        store.dispatch('combat/createCampaignLog', {
          arena: arenaId,
          type: 'Combat',
          description,
        }),
      )
    },
    async queueCampaignLogWrite(writeOperation) {
      const previousWrite = this.campaignLogQueue || Promise.resolve()
      const nextWrite = previousWrite
        .catch(() => {})
        .then(() => writeOperation())

      this.campaignLogQueue = nextWrite.catch(() => {})

      return nextWrite
    },
    async writeArenaEventLog(description = '') {
      const arenaId = this.arena?.id || this.getCampaign()?.id

      if (!arenaId || !description) {
        return
      }

      await this.queueCampaignLogWrite(() =>
        store.dispatch('combat/createCampaignLog', {
          arena: arenaId,
          type: 'Combat',
          description,
        }),
      )
    },
    async showCombatMessage(payload = '') {
      const displayMessage =
        typeof payload === 'string' ? payload : (payload?.message ?? '')

      const logContext = this.getCombatLogContext({
        round: typeof payload === 'string' ? null : payload?.round,
        actionNumber:
          typeof payload === 'string' ? null : payload?.actionNumber,
        extraAttacks:
          typeof payload === 'string' ? undefined : payload?.extraAttacks,
      })

      const logMessage =
        typeof payload === 'string'
          ? formatCombatMessage({
              round: logContext.round,
              actionNumber: logContext.actionNumber,
              message: payload,
              extraAttacks: logContext.extraAttacks,
            })
          : formatCombatMessage({
              round: logContext.round,
              actionNumber: logContext.actionNumber,
              combatantName: payload?.combatantName,
              message: payload?.message,
              extraAttacks: logContext.extraAttacks,
            })

      if (!displayMessage) {
        return
      }

      showNotification(displayMessage, { log: false })

      const arenaId = this.arena?.id || this.getCampaign()?.id
      if (!arenaId || !logMessage) {
        return
      }

      await this.queueCampaignLogWrite(() =>
        store.dispatch('combat/createCampaignLog', {
          arena: arenaId,
          type: 'Combat',
          description: logMessage,
        }),
      )
    },
    //
    //************************************
    //
    //             INIT ARENA
    //
    //************************************
    //
    //========================================================
    // enter/leave arena
    //-------------------
    SetView(view) {
      this.setCurrentView(view)
    },
    async showHiddenRollPopup(message = {}) {
      const currentUser = this.$store.getters['auth/getUserProfile'] || {}
      const currentUserIds = [currentUser?.id, currentUser?.email]
        .filter(
          (value) => value !== null && value !== undefined && value !== '',
        )
        .map((value) => String(value))

      if (!currentUserIds.includes(String(message?.dm_id))) {
        return false
      }

      await this.$refs.confirmDialog?.show?.({
        title: 'Hidden Roll to DM',
        message: `from ${message?.player_name || 'Player'}`,
        subject: formatHiddenRollValue(message?.roll),
        subjectReadonly: true,
        okButton: 'OK',
        horizontalOffset: '-10vw',
        zIndex: 12000,
      })

      return true
    },
    isCurrentUserHiddenRollRecipient(message = {}) {
      const currentUser = this.$store.getters['auth/getUserProfile'] || {}
      const currentUserIds = [currentUser?.id, currentUser?.email]
        .filter(
          (value) => value !== null && value !== undefined && value !== '',
        )
        .map((value) => String(value))

      return currentUserIds.includes(String(message?.dm_id))
    },
    acknowledgeHiddenRollDelivery(connection, message = {}) {
      if (
        !message?.request_id ||
        !connection ||
        connection.readyState !== WebSocket.OPEN
      ) {
        return
      }

      connection.send(
        JSON.stringify({
          message: {
            type: 'hidden_roll_ack',
            request_id: message.request_id,
          },
        }),
      )
    },
    ensureArenaSocketConnection() {
      const arenaSlug = this.arena_slug

      if (!arenaSlug) {
        return
      }

      const existingConnection = this.arenaSocketConnection
      if (
        existingConnection &&
        existingConnection.bbArenaSlug === arenaSlug &&
        (existingConnection.readyState === WebSocket.OPEN ||
          existingConnection.readyState === WebSocket.CONNECTING)
      ) {
        return
      }

      if (
        existingConnection &&
        existingConnection.readyState === WebSocket.OPEN
      ) {
        existingConnection.close(1000)
      }

      const connection = createSocketConnection('arena', arenaSlug)
      connection.bbArenaSlug = arenaSlug
      connection.onmessage = (event) => {
        const data = JSON.parse(event.data)
        const message = data.message

        console.log('combat arena socket message = ', message)

        if (message?.type === 'hidden_roll_delivery') {
          if (this.isCurrentUserHiddenRollRecipient(message)) {
            console.log(
              'combat hidden roll ack -> request_id = ',
              message.request_id,
            )
            this.acknowledgeHiddenRollDelivery(connection, message)
          }
          void this.showHiddenRollPopup(message)
        }
      }

      this.arenaSocketConnection = connection
    },
    //========================================================
    // get campaign progress
    //--------------------
    getCampaignProgress() {
      console.log('----------------------')
      console.log('IN getCampaignProgress')
      this.arena = this.getCampaign()
      if (!this.arena) {
        return false
      }
      //
      this.arena_status = this.arena.status
      this.arena_round = this.arena.round
      this.arena_action = this.arena.action
      this.captureLoggableActionNumber(this.arena_action)
      this.arena_slug = this.arena.slug
      this.arena_rolled = this.arena.rolled
      this.arena_in_progress = this.arena.in_progress
      this.arena_round_completed = this.arena.round_completed
      this.arena_locked = this.arena.locked
      this.arena_ended = this.arena.ended
      //
      this.view_round = this.arena.round
      setViewRound(this.view_round)
      return true
    },
    //========================================================
    // clear debug log harness
    //-------------------------
    async clearDebugLogHarness() {
      console.log('-------------------------')
      console.log('IN clearDebugLogHarness')
      //------
      // call
      //------
      let call = this.clearDebugLog()
      await call
        //-------------
        // catch error
        //-------------
        .catch((err) => {
          handleError(err, 42)
        })
    },
    //========================================================
    // clear arena log harness
    //-------------------------
    async clearArenaLogHarness() {
      console.log('-------------------------')
      console.log('IN clearArenaLogHarness')
      let arena_slug = this.arena_slug
      //------
      // call
      //------
      let call = this.clearArenaLog(arena_slug)
      await call
        //-------------
        // catch error
        //-------------
        .catch((err) => {
          handleError(err, 44)
        })
    },
    //========================================================
    // init arena
    //------------
    async initArena(round) {
      console.log('-------------------------')
      console.log('IN initArena')
      console.log('initArena round = ', round)
      this.loadReady = false
      //------------------
      // load campaign slots
      //------------------
      let call = this.loadArenaRoundSlotsHandler(round)
      await call
        //------------
        // successful
        //------------
        .then(async (combatants) => {
          console.log(
            'initArena raw combatants from loadArenaRoundSlotsHandler = ',
            combatants.map((combatant) => ({
              name: combatant.name,
              slug: combatant.slug,
              status: combatant.status,
            })),
          )
          //----------------------------
          // load local combatant props
          //----------------------------
          this.combatants = loadLocalCombatantProps(combatants)
          console.log(
            'initArena after loadLocalCombatantProps = ',
            this.combatants.map((combatant) => ({
              name: combatant.name,
              slug: combatant.slug,
              prev_round_action_image: combatant.prev_round_action_image,
            })),
          )
          //-----------------------
          // load combatant images
          //-----------------------
          loadCombatantImages()
          await this.loadPreviousRoundActionImages(round)
          console.log(
            'initArena after initial loadPreviousRoundActionImages = ',
            this.combatants.map((combatant) => ({
              name: combatant.name,
              prev_round_action_image: combatant.prev_round_action_image,
            })),
          )
          //---------------------------------
          // load combatant headline markers
          //---------------------------------
          loadCombatantHeadlineMarkers()
          //-------------------------
          // refresh combatant order
          //-------------------------
          this.combatants = refreshCombatantOrder(this.combatants)
          console.log(
            'initArena after refreshCombatantOrder = ',
            this.combatants.map((combatant) => ({
              name: combatant.name,
              prev_round_action_image: combatant.prev_round_action_image,
            })),
          )
          //-----------------------------------
          // get active combatant status count
          //-----------------------------------
          let actives = this.getActiveCombatantCount()
          //--------------------------------
          // check if any active combatants
          //--------------------------------
          if (actives == 0) {
            this.filterStatus = false
          } else {
            this.filterStatus = true
          }
          //--------------
          // filter table
          //--------------
          let curFilterStatusDesc = getFilterStatusDesc(this.filterStatus)
          this.filterCombatantStatuses(curFilterStatusDesc)
          console.log(
            'initArena after filterCombatantStatuses before reload = ',
            this.combatants.map((combatant) => ({
              name: combatant.name,
              prev_round_action_image: combatant.prev_round_action_image,
            })),
          )
          await this.loadPreviousRoundActionImages(round)
          console.log(
            'initArena after second loadPreviousRoundActionImages = ',
            this.combatants.map((combatant) => ({
              name: combatant.name,
              prev_round_action_image: combatant.prev_round_action_image,
            })),
          )
          //------------------
          // manage scenarios
          //------------------
          this.manageScenarios()
          //-----------------------
          // show summary headings
          //-----------------------
          this.showSummaryHeadings()
        })
        //-------------
        // catch error
        //-------------
        .catch((err) => {
          handleError(err, 32)
        })
    },
    //========================================================
    // manage scenarios
    //------------------
    async manageScenarios(ids = null) {
      console.log('----------------------')
      console.log('IN manageScenarios')
      //======================================================
      // SCENARIO A: Not i/p + no o/s rolls - Just show table
      //------------------------------------------------------
      if (!this.arena_in_progress && !this.outstanding_rolls) {
        console.log('SCENARIO A - show table...')
        //------------
        // show table
        //------------
        this.loadReady = true
        //===================
        // SCENARIO B: i/p
        //-------------------
      } else {
        console.log('SCENARIO B - i/p - getting cur action...')
        //-------------------
        // get current action
        //--------------------
        this.getCurrentActionHarness().then(() => {
          // console.log("calling getCurrentActionHarness 6...");
          //======================================================
          // SCENARIO C: check missing actions for ALL combatants
          //------------------------------------------------------
          if (this.missing_check) {
            console.log('SCENARIO C - off to check missing actions...')
            this.manageMissingActions(ids)
          }
          //=================================================
          // SCENARIO D: have o/s rolls (e.g. deployed char)
          //-------------------------------------------------
          if (this.outstanding_rolls) {
            console.log('SCENARIO D - o/s rolls...')
            //-----------------
            // prompt sr rolls
            //-----------------
            this.manageRolls()
            //------------
            // show table
            //------------
            this.loadReady = true
          } else {
            //=======================================
            // SCENARIO E: no o/s rolls - show table
            //---------------------------------------
            console.log('SCENARIO E - NO o/s rolls...')
            //------------
            // show table
            //------------
            this.loadReady = true
          }
        })
      }
    },
    //========================================================
    // delete combatant slots harness
    //--------------------------------
    async deleteCombatantSlotsHarness(parm) {
      console.log('-----------------------')
      console.log('IN deleteCombatantSlotsHarness')
      //-----------------------
      // call delete all slots
      //-----------------------
      let call = useDeleteCombatantSlots(parm)
      await call
        .then(() => {})
        .catch((err) => {
          handleError(err, 38)
        })
    },
    //========================================================
    // load campaign round slots handler
    //--------------------------------
    async loadArenaRoundSlotsHandler(round) {
      console.log('--------------------')
      console.log('IN loadArenaRoundSlotsHandler...')
      return new Promise((resolve) => {
        //------------------
        // load campaign slots
        //------------------
        let call = loadCampaignRoundSlots(round)
        call
          .then((combatants) => {
            resolve(combatants)
          })
          .catch((err) => {
            handleError(err, 33)
          })
      })
    },
    //
    //************************************
    //
    //          LOAD STATIC DATA
    //
    //************************************
    //
    //========================================================
    // load config handler
    //---------------------
    async loadConfigHandler() {
      // console.log("---------------------");
      // console.log("IN loadConfigHandler");
      //----------
      // api call
      //----------
      await this.loadConfig()
        //------------
        // successful
        //------------
        .then(() => {
          this.config = this.getConfig()
          //----------------
          // set debug mode
          //----------------
          this.debug = this.config.debug
          //------------------------------
          // switch off extra action mode
          //------------------------------
          this.arena_extra_action = 0
          this.arena_extra_action_save = 0
          this.arena_extra_action_ready = false
          //----------------
          // set max CA/CR
          //----------------
          this.maxCA = this.config.max_ca
          this.maxCR = this.config.max_cr
          //----------------
          // set max CA/CR
          //----------------
          // load table heading dropdowns (categories)
          // this.headings[8].filterOptions.filterDropdownItems =
          // this.config.combatant_categories;
          // load table heading dropdowns (types)
          // this.headings[9].filterOptions.filterDropdownItems =
          // this.config.types;
          //------------------
          // load action cols
          //------------------
          this.loadActionCols()
          //--------------------
          // load reaction cols
          //--------------------
          this.loadReactionCols()
          //--------------------------------
          // apply combat slot visibility
          //--------------------------------
          this.refreshCombatActionReactionCols()
        })
        //--------
        // error
        //--------
        .catch((err) => {
          this.destroyToken()
          this.destroyUserProfile()
          this.setMessage(window.bbErrorMsg(err))
          this.$router.push({ name: 'logoutView' })
        })
    },
    //========================================================
    // load arena handler
    //--------------------
    async loadArenaHandler(arena_slug) {
      console.log('--------------------')
      console.log('IN loadArenaHandler')

      const allPromise = Promise.all([
        this.loadConfigHandler(),
        this.loadStaticStatuses(),
        this.loadStaticMarkers(),
        this.loadStaticOptions(),
        this.loadStaticVisibilities(),
        this.loadStaticFatigues(),
        this.loadStaticActions(),
        this.loadStaticReactions(),
        this.loadStaticRaces(),
        this.loadStaticRoles(),
        this.loadCampaignPlayers(arena_slug),
        this.loadCampaign(arena_slug),
      ])
      //---------
      // results
      //---------
      allPromise
        .then(() => {
          if (!this.getCampaignProgress()) {
            throw new Error('Campaign failed to load')
          }

          const currentUser = this.$store.getters['auth/getUserProfile'] || {}
          const arenaDmUserName = this.arena?.dm?.user_name || ''

          if (this.debug && currentUser.user_name === arenaDmUserName) {
            this.clearDebugLogHarness()
          }

          this.ensureArenaSocketConnection()
          //-----------------------
          // get stored combatants
          //-----------------------
          this.combatants = this.getCombatants()
          //========================================
          // where combatants exist
          //------------------------
          if (this.combatants.length > 0) {
            console.log('combatants exist = ' + this.combatants.length)
            //------------------
            // init arena round
            //------------------
            this.initArena(this.arena_round)
          } else {
            //----------------------------
            // no combatants - show table
            //----------------------------
            this.loadReady = true
          }
          //-----------
          // get races
          //-----------
          this.races = this.getRaces()
          //-------------
          // get actions
          //-------------
          this.actions = this.getActions()
          //---------------
          // get reactions
          //---------------
          this.reactions = this.getReactions()
          //-------------
          // get markers
          //-------------
          this.markers = this.getMarkers()
          //------------------------------
          // set view round = arena round
          //------------------------------
          this.view_round = changeViewRound()
          //------------------------
          // enable actions palette
          //------------------------
          this.loadActionsReady = true
          //-------------------
          // update scoreboard
          //-------------------
          this.emitter.emit('update-scoreboard')
          //----------------------
          // check all srs rolled   [catch deployed chars]
          //----------------------
          // this.checkAllSRRolled();
          //------------------
          // set player names
          //------------------
          let player_names = extractPlayerNames(this.arena.players)
          this.setPlayerNames(player_names)
          // this.headings[7].filterOptions.filterDropdownItems = player_names;
        })
        .catch((err) => {
          handleError(err, 34)
        })
    },
    //========================================================
    // use update arena progress harness
    //-----------------------------------
    async useUpdateArenaProgressHarness(
      progress_parm,
      message = '',
      options = {},
    ) {
      // console.log("-----------------------");
      // console.log("IN useUpdateArenaProgressHarness");
      //-----------------------
      // update arena progress
      //-----------------------
      let call = useUpdateArenaProgress(progress_parm)
      await call
        .then(() => {
          //-------------------
          // update scoreboard
          //-------------------
          this.emitter.emit('update-scoreboard')
          //--------------
          // show message
          //--------------
          if (message) {
            showNotification(message)
          }
          if (options?.combatMessage) {
            void this.showCombatMessage(options.combatMessage).catch((err) => {
              handleError(err, 56)
            })
          }
        })
        .catch((err) => {
          handleError(err, 35)
        })
    },
    //========================================================
    // get current action harness
    //----------------------------
    async getCurrentActionHarness(deleted_slot = false) {
      console.log('-----------------------')
      console.log('IN getCurrentActionHarness')

      if (this.view_round != this.arena_round) {
        return
      }

      if (this.arena_bleeders_shown) {
        this.arena_extra_ready = false
        this.arena_extra_action = 0
        this.emitter.emit('update-scoreboard')
        return
      }

      let delay_cnt = 0
      //--------------------------------------
      // get current action for current round
      //--------------------------------------
      let return_parm = getCurrentAction(this.combatants)
      // console.log(
      //   "return getCurrentAction parm - unused_action_count = " +
      //     return_parm.unused_action_count
      // );
      //---------------------------------------
      // set unused action cnt for this action
      //---------------------------------------
      // this.arena_action = return_parm.cur_action;
      this.unused_action_count = return_parm.unused_action_count
      this.setUnusedActionCount(return_parm.unused_action_count)
      //---------------------------
      // A. check for end of round
      //---------------------------
      if (return_parm.cur_action == 99) {
        this.captureLoggableActionNumber(this.arena_action)
        delay_cnt = hasDelays(this.combatants, this.arena_action)
        // console.log("DELAYS end round = " + delay_cnt);
        //------------------
        // check for delays
        //------------------
        if (delay_cnt > 0) {
          //--------------
          // show message
          //--------------
          showNotification('Cannot proceed - delays still present')
        } else {
          //---------------------------
          // get extra attacks harness
          //---------------------------
          this.getExtraAttacksHarness()
          //------------
          // show table
          //------------
          this.loadReady = true
        }
        //----------------------
        // B. cur action change
        //----------------------
      } else if (
        return_parm.cur_action > this.stored_cur_action ||
        (deleted_slot && this.arena_action != this.stored_cur_action)
      ) {
        delay_cnt = hasDelays(this.combatants, this.arena_action)
        // console.log("DELAYS end of action = " + delay_cnt);
        //------------------
        // check for delays
        //------------------
        if (delay_cnt > 0) {
          //--------------
          // show message
          //--------------
          showNotification('Cannot proceed - delays present')
        } else if (this.arena_action > 0) {
          const nextAction = return_parm.cur_action
          //-------------------
          // update new action
          // (could be the same as prev where deployed chars or i->a)
          //-------------------
          this.arena_action = nextAction
          this.newActionHarness(deleted_slot)
          //--------------------
          // set current action
          //--------------------
          this.setCurrentActionNumber(this.arena_action)
        }
      }
      // ------------------
      // update scoreboard
      // ------------------
      if (delay_cnt == 0) {
        //--------------------------------------------------
        // set arena action for this action where no delays
        //--------------------------------------------------
        this.arena_action = return_parm.cur_action
        this.emitter.emit('update-scoreboard')
      }
    },
    //========================================================
    // get extra attacks harness
    //---------------------------
    // a) has second weapon OR shield
    // b) has o/s reaction(s)
    // c) is active + not on counters
    // NB: DM decides if extra attack possible
    //-----------------------------------------
    async getExtraAttacksHarness() {
      console.log('-----------------------')
      console.log('IN getExtraAttacksHarness')
      const previousSubStatus = this.arenaSubStatus
      this.arena_extra_ready = false
      //----------------
      // set call parm
      //----------------
      let extra_parm = {
        arena_slug: this.arena_slug,
        round: this.arena_round,
      }
      //-------------------
      // get extra attacks
      //-------------------
      let call = getExtraAttacks(extra_parm)
      await call
        .then((extras) => {
          this.combatants = setLocalCombatantsExtras(this.combatants, extras)
          console.log('getExtraAttacksHarness - extras = ', extras)
          console.log(
            'getExtraAttacksHarness - this.combatants = ',
            this.combatants,
          )
          //---------------------------------------------
          // switch to extra action mode + set extra ind
          //---------------------------------------------
          if (extras.length > 0) {
            this.arena_extra_action = this.ca_extra
            this.arena_extra_ready = true
            console.log(
              'getExtraAttacksHarness - arena_extra_ready = ',
              this.arena_extra_ready,
            )
            //--------------
            // show message
            //--------------
            if (shouldNotifyExtraAttacksReady(previousSubStatus)) {
              showNotification('Extra attacks ready')
            }
          } else {
            //--------------
            // show messages
            //--------------
            showNotification('No extra attacks available')
            //---------------------------------
            // check for bleeders (in/actives)
            //---------------------------------
            this.arena_bleeders_ready = hasBleeders(this.getCombatants())
            //---------------------------------
            // check for bleeders (in/actives)
            //---------------------------------
            if (this.arena_bleeders_ready) {
              showNotification('Bleeders present')
            } else if (!this.shouldAutoStartNewRound()) {
              showNotification('Start new round')
            }
            //-------------
            // round ended
            //-------------
            this.endRoundHarness()
            if (!this.arena_bleeders_ready && this.shouldAutoStartNewRound()) {
              this.checkRetainSRRolls()
            }
          }
        })
        .catch((err) => {
          handleError(err, 37)
        })
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    //--------------
    // enter arena
    //--------------
    this.SetView('dm_combat')
    //--------------------
    // load arena handler
    //--------------------
    this.loadArenaHandler(this.slug)
    //
    //************************************
    //
    //             EMMITERS
    //
    //************************************
    //
    //========================================================
    // show notification
    //-------------------
    this.emitter.on('show-notification', (msg) => {
      //--------------
      // show message
      //--------------
      showNotification(msg)
    })
    //========================================================
    // show combat message
    //--------------------
    this.emitter.on('show-combat-message', (msg) => {
      void this.showCombatMessage(msg).catch((err) => {
        handleError(err, 47)
      })
    })
    //========================================================
    // show combatant panel handler
    //------------------------------
    this.emitter.on('show-combatant-panel', () => {
      this.showCombatantPanel()
    })
    //========================================================
    // toggle filter status handler
    //------------------------------
    this.emitter.on('toggle-filter-status', () => {
      console.log('IN emit toggle-filter-status')
      this.filterStatus = !this.filterStatus
    })
    //========================================================
    // toggle description headings handler
    //-------------------------------------
    this.emitter.on('toggle-description-headings', () => {
      console.log('IN toggle-description-headings')
      this.toggleDescHeadings()
    })
    //========================================================
    // sidepanel detect emit
    //-----------------------
    this.emitter.on('toggle-sidepanel', (id) => {
      this.combatant = getCombatantById(id)
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    })
    //========================================================
    // next combatant emit
    //---------------------
    this.emitter.on('next-combatant', (combatant) => {
      this.combatant = getNext(combatant.id, this.combatants)
      this.emitter.emit('load-combatant', this.combatant)
    })
    //========================================================
    // previous combatant emit
    //-------------------------
    this.emitter.on('previous-combatant', (combatant) => {
      this.combatant = getPrevious(combatant.id, this.combatants)
      this.emitter.emit('load-combatant', this.combatant)
    })
    //========================================================
    // close side panel
    //------------------
    this.emitter.on('close-combatant-side-panel', (ind) => {
      this.isSidePanelOpen = ind
    })
    //========================================================
    // add combatants
    //----------------
    this.emitter.on('add-combatants', (args) => {
      //-------------------------------------
      // call multiple combatant add harnass
      //-------------------------------------
      this.addMultipleCombatantsHarness(args)
    })
    //========================================================
    // update combatant
    //------------------
    this.emitter.on('update-combatant', (update_parm) => {
      console.log('------------------')
      console.log('IN update-combatant')
      //-----------------
      // isolate payload
      //-----------------
      let payload = update_parm.payload
      this.originalCombatantPayload = update_parm.originalPayload || {}
      //-------------------
      // prime change type
      //-------------------
      if (payload.status == 'Inactive') {
        update_parm.type = 'status'
      } else {
        update_parm.type = ''
      }
      //--------------------------
      // check for change payload
      //--------------------------
      payload = changePayload(update_parm)
      //-----------------------------------------------------------
      // force re-filter of cur filter status where sub-status chg
      //-----------------------------------------------------------
      this.forceRefilterCheck = update_parm.forceRefilterCheck
      //---------------------------------
      // perform combatant call (update)
      //---------------------------------
      this.updateCombatantHarness(payload)
    })
    //========================================================
    // delete combatant
    //------------------
    this.emitter.on('delete-combatant', (id) => {
      //---------------------------------
      // perform combatant call (delete)
      //---------------------------------
      this.deleteCombatantHarness(id)
    })
    //========================================================
    // update combatant fields
    //-------------------------
    this.emitter.on('update-combatant-fields', (update_parm) => {
      console.log('-----------------------------')
      console.log('IN update-combatant-fields update_parm = ', update_parm.type)
      const notificationMessage = formatCombatantUpdateMessage({
        payload: update_parm.payload,
        originalPayload: update_parm.originalPayload,
        type: update_parm.type,
      })
      //-----------------
      // isolate payload
      //-----------------
      let payload = update_parm.payload
      //--------------------------
      // check for change payload
      //--------------------------
      payload = changePayload(update_parm)
      //----------------------------------------------------
      // where status = inactive = delete re/actions/markers
      //-----------------------------------------------------
      if (
        update_parm.type == 'status' &&
        update_parm.payload.status == 'Inactive'
      ) {
        this.deleteCombatantSlotsHarness(update_parm.payload.slug)
      }
      //--------------------
      // check for refilter
      //--------------------
      this.forceRefilterCheck = update_parm.forceRefilterCheck
      console.log(
        'IN update-combatant-fields forceRefilterCheck = ',
        this.forceRefilterCheck,
      )
      //-------------------------------------------
      // perform combatant selective fields update
      //-------------------------------------------
      this.updateCombatantFieldsHarness(
        payload,
        update_parm.id,
        notificationMessage,
      )
    })
    //========================================================
    // panel action selected
    //-----------------------
    this.emitter.on('panel-action-selected', (action_name) => {
      //---------------------------------------------------
      // get action object from action name where not blank
      //----------------------------------------------------
      if (action_name != '') {
        //----------------------------------
        // save action image for selections
        //----------------------------------
        this.selected_action = getAction(action_name)
        //---------------
        // reset markers
        //---------------
        if (this.selected_marker) {
          this.emitter.emit('reset-panel-markers', this.selected_marker)
        }
        //--------------------
        // reset local marker
        //--------------------
        this.selected_marker = null
      } else {
        this.selected_action = null
      }
    })
    //========================================================
    // panel reaction selected
    //-------------------------
    this.emitter.on('panel-reaction-selected', (reaction_name) => {
      //--------------------------------------------------------
      // get reaction object from reaction name where not blank
      //--------------------------------------------------------
      if (reaction_name != '') {
        //------------------------------------
        // save reaction image for selections
        //------------------------------------
        this.selected_reaction = getReaction(reaction_name)
      } else {
        this.selected_reaction = null
      }
    })
    //========================================================
    // panel marker selected
    //-----------------------
    this.emitter.on('panel-marker-selected', (marker_name) => {
      //----------------------------------------------------
      // get marker object from marker name where not blank
      //----------------------------------------------------
      if (marker_name != '') {
        //----------------------------------
        // save marker image for selections
        //----------------------------------
        this.selected_marker = getMarker(marker_name)
        //---------------------
        // reset panel actions
        //---------------------
        if (this.selected_action) {
          this.emitter.emit('reset-panel-actions', this.selected_action)
        }
        //--------------------
        // reset local action
        //--------------------
        this.selected_action = null
      } else {
        this.selected_marker = null
      }
    })
    //========================================================
    // panel number selected
    //-----------------------
    this.emitter.on('panel-number-selected', (selection_parm) => {
      this.selected_number = selection_parm.number
      this.selected_number_image = selection_parm.image
    })
    //========================================================
    // combatant action selected
    //---------------------------
    this.emitter.on('combatant-action-selected', (cur_action_parm) => {
      console.log('-----------------------')
      console.log('combatant-action-selected...')
      // for (var key of Object.keys(cur_action_parm)) {
      //   console.log(key + " -> " + cur_action_parm[key]);
      // }
      //----------------------------
      // action selected from panel
      //----------------------------
      if (this.selected_action) {
        //-------------------------------------------------------
        // perform action call (add/update where current...
        // a) slug = blank
        // b) overriding existing ca slot with diff panel action
        //-------------------------------------------------------
        if (
          cur_action_parm.slot_slug == '' ||
          cur_action_parm.slot_name != this.selected_action.name
        ) {
          this.prepActionCall(cur_action_parm, this.selected_action)
        }
      }
    })
    //========================================================
    // combatant marker selected
    //---------------------------
    this.emitter.on('combatant-marker-selected', (cur_action_parm) => {
      console.log('-------------------------------')
      console.log('IN combatant-marker-selected')
      //----------------------------
      // marker selected from panel
      //----------------------------
      if (this.selected_marker) {
        //-------------------------------------------------------
        // perform marker call - add/update where current...
        // a) slug = blank
        // b) overriding existing ca slot with diff panel marker
        //-------------------------------------------------------
        if (
          cur_action_parm.slot_slug == '' ||
          cur_action_parm.slot_name != this.selected_marker.name
        ) {
          this.prepMarkerCall(cur_action_parm)
        }
      }
    })
    //========================================================
    // delete CA
    //-----------
    this.emitter.on('delete-ca', (parm) => {
      console.log('-------------------')
      console.log('delete-ca')
      //--------------------------
      // perform ca call (delete)
      //--------------------------
      this.deleteSlotHarness(parm)
      //----------------------
      // reduce action counts
      //----------------------
      let reduce_parm = {
        id: parm.id,
        actual: parm.actual,
      }
      reduceActionCount(reduce_parm)
      //--------------------------------------------------------
      // shift cas when deleting where not in extra action mode
      //--------------------------------------------------------
      if (this.arena_extra_action == 0) {
        let delete_markers_array = shiftCAsForDeletion(parm.id, parm.num)
        //-------------------------------------
        // delete markers if slot is now blank
        //-------------------------------------
        if (delete_markers_array.length > 0) {
          this.deleteBlankSlotMarkers(delete_markers_array)
        }
        //----------------------------
        // where in extra action mode
        //----------------------------
      } else {
        let deleted_action = true
        let idx = getCombatantFilterIndexById(this.combatants, parm.id)
        loadSpecificCombatantExtraImage(idx, deleted_action, this.combatants)
      }
    })
    //========================================================
    // delete marker
    //---------------
    this.emitter.on('delete-marker', (parm) => {
      //------------------------------
      // perform marker call (delete)
      //------------------------------
      this.deleteSlotHarness(parm)
      //----------------------
      // reduce markers count
      //----------------------
      reduceMarkersCount(parm.id)
      //-----------------------
      // reduce counters count
      //-----------------------
      if (parm.marker_name == 'counter') {
        reduceCountersCount(parm.id)
      }
      //------------------
      // set pending slot
      //------------------
      let slot_parm = {
        id: parm.id,
        ca_num: parm.num,
      }
      setPendingCA(slot_parm)
    })
    //========================================================
    // delete CR
    //-----------
    this.emitter.on('delete-cr', (parm) => {
      //----------------------------
      // perform cr call (delete)
      //----------------------------
      this.deleteSlotHarness(parm)
      //----------------------
      // reduce action counts
      //----------------------
      reduceReactionCount(parm.id)
      //------------------
      // set pending slot
      //------------------
      let slot_parm = {
        id: parm.id,
        cr_num: parm.num,
      }
      setPendingCR(slot_parm)
      //-------------------------------
      // poss fill blanks crs to right
      //-------------------------------
      fillBlankCRs(slot_parm)
    })
    //========================================================
    // combatant reaction selected
    //-----------------------------
    this.emitter.on('combatant-reaction-selected', (reaction_parm) => {
      //----------------------------
      // reaction selected from panel
      //----------------------------
      if (this.selected_reaction) {
        //-------------------------------------------------------
        // perform reaction call (add/update where ...
        // a) slug = blank
        // b) overriding existing cr slot with diff panel action
        //-------------------------------------------------------
        if (
          reaction_parm.slot_slug == '' ||
          reaction_parm.slot_name != this.selected_reaction.name
        ) {
          this.prepReactionCall(reaction_parm)
        }
      }
      //----------------------------
      // set last selected reaction
      //----------------------------
      var num = reaction_parm.slot.substr(2, 1)
      this.last_selected_reaction.id = reaction_parm.id
      this.last_selected_reaction.cr = num
    })
    //========================================================
    // combatant sr rolled
    //---------------------
    this.emitter.on('combatant-sr-rolled', (sr_parm) => {
      // console.log("----------------------");
      // console.log("IN combatant-sr-rolled roll = " + sr_parm.roll);
      // this.checkCombatantsExist(this.combatants);
      //---------------------
      // get local combatant
      //---------------------
      let idx = getCombatantFilterIndexById(this.combatants, sr_parm.id)
      //----------------------
      // trap not found error
      //----------------------
      if (idx == -1) {
        alert(
          'Error - not found local combatant name = ' +
            sr_parm.name +
            ' id = ' +
            sr_parm.id,
        )
      }
      //----------------
      // check sr field
      //----------------
      if (sr_parm.field == 'sr_roll_image') {
        //------------------
        // sr roll selected
        //------------------
        this.selectedSRRoll(idx, sr_parm.roll)
        //-------------------
        // roll-off selected
        //-------------------
      } else {
        this.selectedRollOff(idx, sr_parm.roll)
      }
    })
    //========================================================
    // next view round
    //-----------------
    this.emitter.on('next-view-round', () => {
      this.view_round = changeViewRound('next')
    })
    //========================================================
    // previous view round
    //---------------------
    this.emitter.on('previous-view-round', () => {
      this.view_round = changeViewRound('previous')
    })
    //========================================================
    // extra attacks
    //---------------
    this.emitter.on('extra-attacks', () => {
      //--------------------------------
      // get extra attacks confirmation
      //--------------------------------
      this.confirmEvent('Extra Attacks')
    })
    //========================================================
    // bleeders at end of round
    //--------------------------
    this.emitter.on('round-bleeders', () => {
      console.log('emit round-bleeders...')
      //-----------------------
      // switch off extra cols
      //-----------------------
      if (this.toggleExtras) {
        console.log('turn off extra cols...')
        this.toggleExtras = !this.toggleExtras
      }
      //-------------------------------
      // filter combatants by bleeders
      //-------------------------------
      this.showBleederCombatants()
      //------------------------
      // force summary headings
      //------------------------
      this.showSummaryHeadings()
      //-----------
      // set flags
      //-----------
      this.arena_extra_ready = false
      this.arena_extra_action = 0
      this.arena_bleeders_ready = false
      this.arena_bleeders_shown = true
    })
    //========================================================
    // unlock campaign
    //--------------
    this.emitter.on('unlock-arena', () => {
      //-------------------------------
      // get unlock campaign confirmation
      //-------------------------------
      this.confirmEvent('Unlock Campaign')
    })
    //========================================================
    // lock campaign
    //------------
    this.emitter.on('lock-arena', () => {
      //-----------------------------
      // get lock campaign confirmation
      //-----------------------------
      this.confirmEvent('Lock Campaign')
    })
    //========================================================
    // new combat
    //-----------
    this.emitter.on('new-combat', () => {
      //-----------------------------
      // get new combat confirmation
      //-----------------------------
      this.confirmEvent('New Combat')
    })
    //========================================================
    // new round
    //-----------
    this.emitter.on('new-round', () => {
      // console.log("--------------------");
      // console.log("emitter new round");
      this.triggerNextRound()
    })
    //========================================================
    // sr rolls
    //----------
    this.emitter.on('sr-rolls', () => {
      // console.log("---------------------");
      // console.log("IN sr-rolls");
      //--------------
      // manage rolls
      //--------------
      this.manageRolls()
    })
    //========================================================
    // start combat
    //--------------
    this.emitter.on('start-combat', () => {
      //---------------------------
      // start combat confirmation
      //---------------------------
      this.confirmEvent('Start Combat')
    })
    //========================================================
    // end combat
    //------------
    this.emitter.on('end-combat', () => {
      //-------------------------
      // end combat confirmation
      //-------------------------
      this.confirmEvent('End Combat')
    })
    //========================================================
    // roll-off complete
    //-------------------
    this.emitter.on('roll-off-complete', () => {
      console.log('------------------')
      console.log('IN roll-off-complete')
      //-------------------------
      // check for dupe sr rolls
      //-------------------------
      this.managePotentialDupeRolls()
    })
  },
  //========================================================
  // before unmount
  //----------------
  beforeUnmount() {
    if (this.combatTextOverlayTimer) {
      clearTimeout(this.combatTextOverlayTimer)
      this.combatTextOverlayTimer = null
    }

    if (this.arenaSocketConnection?.readyState === WebSocket.OPEN) {
      this.arenaSocketConnection.close(1000)
    }
    this.arenaSocketConnection = null

    this.emitter.off('add-combatants')
    this.emitter.off('update-combatant')
    this.emitter.off('delete-combatant')
    this.emitter.off('update-combatant-fields')

    this.emitter.off('show-combatant-panel')
    this.emitter.off('show-notification')
    this.emitter.off('show-combat-message')

    this.emitter.off('toggle-filter-status')
    this.emitter.off('toggle-description-headings')
    this.emitter.off('toggle-sidepanel')

    this.emitter.off('next-combatant')
    this.emitter.off('previous-combatant')
    this.emitter.off('close-combatant-side-panel')

    this.emitter.off('panel-action-selected')
    this.emitter.off('panel-reaction-selected')
    this.emitter.off('panel-marker-selected')
    this.emitter.off('panel-number-selected')

    this.emitter.off('combatant-action-selected')
    this.emitter.off('combatant-reaction-selected')
    this.emitter.off('combatant-marker-selected')

    this.emitter.off('sr-rolls')
    this.emitter.off('roll-off-complete')
    this.emitter.off('combatant-sr-rolled')

    this.emitter.off('next-view-round')
    this.emitter.off('previous-view-round')
    this.emitter.off('new-round')

    this.emitter.off('start-combat')
    this.emitter.off('end-combat')
    this.emitter.off('new-combat')

    this.emitter.off('reset-panel-actions')
    this.emitter.off('reset-panel-markers')
    this.emitter.off('reset-panel-numbers')
    this.emitter.off('reset-panel-number')

    this.emitter.off('roll-off-complete')

    this.emitter.off('delete-ca')
    this.emitter.off('delete-cr')
    this.emitter.off('delete-marker')

    this.emitter.off('round-bleeders')
  },
}
</script>

<style>
.col-md-1 {
  width: 6% !important;
}

.col-md-2 {
  width: 12% !important;
}

.col-md-9 {
  width: 82% !important;
}

#statusBtn:disabled {
  opacity: 0.3;
}

.bb-previous-round-surface {
  position: relative;
}

.bb-previous-round-surface-disabled {
  pointer-events: none;
}

.bb-previous-round-surface-disabled::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(31, 35, 39, 0.5);
  z-index: 5;
}

.bb-combat-confirm-dialog .bb-hdg {
  font-size: 2rem;
}

.bb-combat-confirm-dialog .bb-sub-hdg,
.bb-combat-confirm-dialog .bb-sub-sub-hdg {
  font-size: 1.55rem;
}

.bb-combat-confirm-dialog .btn {
  font-size: 1.35rem;
}
</style>
